/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from './supabase';
import {
    UserRole,
    Client,
    Currency,
    TaskInstance,
    TaskTemplate,
    TaskCategory,
    TaskStatus,
    TaskType,
    TaskLabel,
    DiaryEntry,
    Payment,
    Rulebook,
    DominaDashboardStats,
    SubDashboardData,
    DailyReport,
    Folder,
    FileItem,
    DailyChecklist,
    DefaultChecklistItem,
    DominaStatusRecord,
    DominaStatus,
    Conversation,
    Message,
    SendMessagePayload,
    ReportTemplate,
    ReportSubmission,
    AppNotification,
    RecurrenceType,
    TaskVisibilityPreset,
    BehavioralFeedback,
    FeedbackType,
    DailyPlan,
    Comment,
    MessageType,
    PaymentMethod,
    PaymentPurpose,
    EmotionDefinition,
    EmotionLog,
    EmotionStats,
    ControlStatus,
    ControlGoal,
    ControlConfig,
    WeeklyControlCheck,
    FlashAlertConfig,
    FlashAlertInstance,
    FlashAlertStatus,
    FlashAlertTriggerType,
    FlashAlertRandomPeriod,
    UserTheme,
    NavigationLayout,
    DailyAudioInput,
    AudioPeriodType,
    AudioIntervalType,
    ReportQuestionType,
    ReportSubmissionFeedback,
    ReportAnswer,
    TodoScope
} from '../types';
import { getWeekInfo, getWeekStartDateFromKey, parseLocalDate } from '../utils/date';

// --- HELPER FUNCTIONS ---
export const getErrorMessage = (error: any): string => {
    if (!error) return 'Unbekannter Fehler';
    if (typeof error === 'string') return error;
    
    let message = 'Unbekannter Fehler';
    if (error instanceof Error) {
        message = error.message;
    } else {
        const err = error as { message?: string; error_description?: string; details?: string };
        message = err.message || err.error_description || (typeof error === 'object' ? JSON.stringify(error) : String(error));
    }

    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        return 'Verbindung zum Server fehlgeschlagen. Bitte prüfe deine Internetverbindung oder ob ein Ad-Blocker die Anfrage blockiert.';
    }
    
    const err = error as { message?: string; details?: string };
    if (err.details) return `${err.message || 'Fehler'}: ${err.details}`;
    
    return message;
};

export const uuid = () => {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch (e) {
        console.warn("crypto.randomUUID failed, using fallback");
    }
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Hilfsfunktion um Daten aus Joins sicher zu extrahieren.
 * Supabase gibt Joins je nach FK-Konfiguration als Objekt oder Array zurück.
 */
const getJoinedData = (data: Record<string, any>, key: string) => {
    if (!data || !data[key]) return null;
    if (Array.isArray(data[key])) return data[key][0];
    return data[key];
};

/**
 * Hilfsfunktion um Listen für PostgREST (Supabase .not('col', 'in', '...')) sicher zu formatieren.
 * Werte mit Leerzeichen müssen in Anführungszeichen stehen.
 */
const formatFilterList = (values: string[]) => `(${values.map(v => v.includes(' ') ? `"${v}"` : v).join(',')})`;

const normalizeStatus = (status: string): TaskStatus => {
    const s = String(status).toUpperCase();
    if (s === 'OFFEN' || s === 'PENDING') return TaskStatus.PENDING;
    if (s === 'IN BEARBEITUNG' || s === 'IN_PROGRESS') return TaskStatus.IN_PROGRESS;
    if (s === 'ERLEDIGT' || s === 'COMPLETED') return TaskStatus.COMPLETED;
    if (s === 'ZUR KONTROLLE' || s === 'PENDING_REVIEW') return TaskStatus.PENDING_REVIEW;
    if (s === 'ÜBERFÄLLIG' || s === 'OVERDUE') return TaskStatus.OVERDUE;
    if (s === 'NICHT ERLEDIGT GESCHLOSSEN' || s === 'CLOSED_INCOMPLETE') return TaskStatus.CLOSED_INCOMPLETE;
    if (s === 'GELÖSCHT' || s === 'DELETED') return TaskStatus.DELETED;
    return TaskStatus.PENDING;
};

const normalizeCategory = (cat: string): TaskCategory => {
    const c = String(cat).toUpperCase();
    if (c === 'SUB AUFGABE' || c === 'SUB' || c === 'SUB_TASK') return TaskCategory.SUB;
    if (c === 'RITUAL' || c === 'RITUALE') return TaskCategory.RITUAL;
    if (c === 'EIGENER TERMIN' || c === 'PERSONAL' || c === 'PRIVATE') return TaskCategory.PERSONAL;
    return TaskCategory.SUB;
};

const normalizeType = (type: string): TaskType => {
    const t = String(type).toUpperCase();
    if (t === 'EINFACH' || t === 'SIMPLE') return TaskType.SIMPLE;
    if (t === 'DAUER' || t === 'DURATION') return TaskType.DURATION;
    if (t === 'ANZAHL' || t === 'COUNT') return TaskType.COUNT;
    if (t === 'INTERVALL' || t === 'INTERVAL') return TaskType.INTERVAL;
    return TaskType.SIMPLE;
};

const normalizeCurrency = (curr: string): Currency => {
    if (!curr) return Currency.EUR;
    const c = String(curr).toUpperCase();
    if (c === 'EUR') return Currency.EUR;
    if (c === 'CHF') return Currency.CHF;
    if (c === 'USD') return Currency.USD;
    if (c === 'GBP') return Currency.GBP;
    // Also check if curr is already a value in the enum
    const values = Object.values(Currency);
    if (values.includes(curr as Currency)) return curr as Currency;
    return Currency.EUR;
};

// --- DB MAPPING FUNCTIONS ---

export const mapClientFromDB = (data: Record<string, any>): Client => {
    const rawPeriodEndDate = data.period_end_date || data.profile?.periodEndDate || undefined;
    const profile = data.profile || { about: '' };
    const rawTimezone = data.timezone || profile.timezone || 'Europe/Berlin';

    const rawRulebookIds: string[] = Array.isArray(profile.rulebookIds) 
        ? profile.rulebookIds 
        : (data.rulebook_id ? [data.rulebook_id] : []);
    const rulebookIds = Array.from(new Set(rawRulebookIds.filter(Boolean)));

    const rawReportTemplateIds: string[] = Array.isArray(profile.reportTemplateIds) 
        ? profile.reportTemplateIds 
        : (data.report_template_id ? [data.report_template_id] : []);
    const reportTemplateIds = Array.from(new Set(rawReportTemplateIds.filter(Boolean)));

    return {
        id: data.id,
        name: data.name,
        email: data.email,
        birthDate: data.birth_date,
        entryDate: data.entry_date,
        periodEndDate: rawPeriodEndDate,
        timezone: rawTimezone,
        isActive: data.is_active,
        isPaused: data.is_paused,
        preferences: data.preferences,
        taboos: data.taboos,
        theme: data.theme as UserTheme,
        lastLogin: data.last_login,
        contact: data.contact,
        financials: data.financials ? {
            ...data.financials,
            currency: normalizeCurrency(data.financials.currency)
        } : { monthlyTribute: 0, tributeFrequency: 'Monatlich', currency: Currency.EUR, includedServices: '' },
        profile: {
            ...profile,
            timezone: rawTimezone,
            periodEndDate: rawPeriodEndDate,
            rulebookIds,
            reportTemplateIds
        },
        goddessNotes: data.goddess_notes,
        rulebookId: data.rulebook_id || rulebookIds[0] || null,
        rulebookIds,
        reportTemplateId: data.report_template_id || reportTemplateIds[0] || null,
        reportTemplateIds,
        telegramChatId: data.telegram_chat_id,
        contract: data.contract || { lastUpdated: new Date().toISOString(), items: [] },
        messages: data.messages || { lastUpdated: new Date().toISOString(), items: [] }
    };
};

export const mapDailyPlanFromDB = (data: Record<string, any>): DailyPlan => ({
    id: data.id,
    clientId: data.client_id,
    date: data.date,
    items: data.items || [],
    checklist: data.checklist || [],
    workStart: data.work_start,
    workEnd: data.work_end,
    wakeUpTime: data.wake_up_time,
    bedTime: data.bed_time,
    isFreeDay: data.is_free_day,
    isSickDay: data.is_sick_day,
    isSealed: data.is_sealed,
    sealedAt: data.sealed_at,
});

export const mapTaskFromDB = (data: Record<string, any>): TaskInstance => {
    const clientData = getJoinedData(data, 'clients');
    const labelData = getJoinedData(data, 'task_labels');
    
    let coachingMeta: any = {};
    let todoMeta: any = {};
    let isTodo = false;
    let todoScope: any = undefined;
    let originalGoddessNotes = data.goddess_notes;

    if (data.goddess_notes && typeof data.goddess_notes === 'string') {
        if (data.goddess_notes.startsWith('__COACHING_META__')) {
            try {
                coachingMeta = JSON.parse(data.goddess_notes.replace('__COACHING_META__', ''));
                originalGoddessNotes = coachingMeta.originalNotes || '';
            } catch (e) {
                // ignore
            }
        } else if (data.goddess_notes.startsWith('__TODO_META__')) {
            try {
                todoMeta = JSON.parse(data.goddess_notes.replace('__TODO_META__', ''));
                isTodo = true;
                todoScope = todoMeta.scope;
                originalGoddessNotes = todoMeta.originalNotes || '';
            } catch (e) {
                isTodo = true;
            }
        }
    }
    if (!isTodo && typeof data.description === 'string' && data.description.includes('__TODO__')) {
        isTodo = true;
    }
    if (!isTodo && typeof data.title === 'string' && data.title.startsWith('📌')) {
        isTodo = true;
    }

    return {
        id: data.id,
        clientId: data.client_id,
        templateId: data.template_id,
        seriesId: data.series_id,
        title: data.title,
        description: data.description,
        category: normalizeCategory(data.category),
        type: normalizeType(data.type),
        status: normalizeStatus(data.status), 
        startDateTime: data.start_date_time,
        endDateTime: data.end_date_time,
        visibilityDate: data.visibility_date,
        completedAt: data.completed_at,
        reviewRequired: data.review_required,
        isReportRequired: data.is_report_required,
        isProofRequired: data.is_proof_required,
        durationTarget: data.duration_target,
        durationProgress: data.duration_progress,
        countTarget: data.count_target,
        countProgress: data.count_progress,
        intervalConfig: data.interval_config || [],
        labelId: data.label_id,
        isMultiDay: data.is_multi_day,
        comments: data.comments || [],
        proofs: data.proofs || [],
        reportContent: data.report_content,
        attachmentLink: data.attachment_link,
        goddessNotes: originalGoddessNotes,
        goddessReminders: data.goddess_reminders || [],
        isBlocking: data.is_blocking,
        clientName: clientData?.name,
        label: labelData ? { id: labelData.id, userId: labelData.user_id, name: labelData.name, color: labelData.color } : null,
        coachingTopicId: coachingMeta.coachingTopicId || data.coaching_topic_id,
        coachingTopicTitle: coachingMeta.coachingTopicTitle || data.coaching_topic_title,
        coachingGoalId: coachingMeta.coachingGoalId || data.coaching_goal_id,
        coachingGoalTitle: coachingMeta.coachingGoalTitle || data.coaching_goal_title,
        coachingReflectionPrompt: coachingMeta.coachingReflectionPrompt || data.coaching_reflection_prompt,
        coachingReflectionAnswer: coachingMeta.coachingReflectionAnswer || data.coaching_reflection_answer,
        coachingCatFeedback: coachingMeta.coachingCatFeedback || data.coaching_cat_feedback,
        isTodo: isTodo || !!data.is_todo,
        todoScope: todoScope || data.todo_scope,
        createdAt: data.created_at,
        updatedAt: data.updated_at
    };
};

export const mapMessageFromDB = (data: Record<string, any>): Message => ({
    id: data.id,
    conversationId: data.conversation_id,
    sender: data.sender as UserRole,
    content: data.content,
    timestamp: data.created_at || data.timestamp,
    isRead: data.is_read,
    isEdited: data.is_edited,
    type: data.type as MessageType,
    reactions: data.reactions || [],
    replyTo: data.reply_to,
    audioUrl: data.audio_url,
    audioDuration: data.audio_duration,
    status: 'sent'
});

export const mapDiaryEntryFromDB = (data: Record<string, any>): DiaryEntry => ({
    id: data.id,
    clientId: data.client_id,
    author: data.author,
    date: data.date,
    endDate: data.end_date,
    type: data.type,
    content: data.content,
    goddessFeedback: data.goddess_feedback ? {
        tags: data.goddess_feedback.tags || [],
        notes: data.goddess_feedback.notes || '',
        isApproved: data.goddess_feedback.isApproved
    } : undefined,
    coachingTopicId: data.coaching_topic_id,
    coachingTopicTitle: data.coaching_topic_title,
    coachingGoalId: data.coaching_goal_id,
    coachingGoalTitle: data.coaching_goal_title
});

export const mapPaymentFromDB = (data: Record<string, any>): Payment => {
    let clientName = data.client_name || data.clients?.name;
    let description = data.description || '';

    // If clientName is not provided by relations, check if it was preserved in the description header e.g. "[Klient: Markus]"
    if (!clientName && description && description.startsWith('[Klient:')) {
        const match = description.match(/^\[Klient:\s*([^\]]+)\]\s*(.*)$/s);
        if (match) {
            clientName = match[1].trim();
            description = match[2].trim();
        }
    }

    return {
        id: data.id,
        clientId: data.client_id || '',
        clientName: clientName || undefined,
        amount: Number(data.amount),
        currency: normalizeCurrency(data.currency),
        method: data.method as PaymentMethod,
        date: data.date,
        purpose: data.purpose as PaymentPurpose,
        description: description,
        tributeStartDate: data.tribute_start_date,
        tributeEndDate: data.tribute_end_date,
    };
};

export const mapControlConfigFromDB = (data: Record<string, any>): ControlConfig => ({
    id: data.id,
    clientId: data.client_id,
    startDate: data.start_date,
    durationWeeks: data.duration_weeks,
    status: data.status as ControlStatus,
    complianceScore: Number(data.compliance_score),
    activeAreas: data.active_areas || [],
    isActive: data.is_active,
    summary: data.summary,
    createdAt: data.created_at,
    updatedAt: data.updated_at
});

export const mapControlGoalFromDB = (data: Record<string, any>): ControlGoal => ({
    id: data.id,
    configId: data.config_id,
    area: data.area,
    title: data.title,
    description: data.description,
    isRequired: data.is_required,
    frequency: (data.frequency as 'WEEKLY' | 'MONTHLY') || 'WEEKLY',
    createdAt: data.created_at
});

export const mapWeeklyControlCheckFromDB = (data: Record<string, any>): WeeklyControlCheck => ({
    id: data.id,
    configId: data.config_id,
    weekNumber: data.week_number,
    submittedAt: data.submitted_at,
    areaChecks: data.area_checks || {},
    revenue: data.revenue ? Number(data.revenue) : undefined,
    energyLevel: data.energy_level,
    eveningEnergyLevel: data.evening_energy_level,
    isCompleted: data.is_completed,
    notes: data.notes,
    dominaNotes: data.domina_notes
});

export const mapFlashAlertConfigFromDB = (data: Record<string, any>): FlashAlertConfig => {
    if (!data) return {} as any;
    return {
        id: data.id,
        clientId: data.client_id,
        title: data.title,
        description: data.description,
        requiresPhoto: data.requires_photo,
        deadlineMinutes: data.deadline_minutes,
        triggerType: data.trigger_type as FlashAlertTriggerType,
        fixedTimes: data.fixed_times || [],
        randomFrequency: data.random_frequency,
        randomPeriod: data.random_period as FlashAlertRandomPeriod,
        randomStartTime: data.random_start_time?.substring(0, 5),
        randomEndTime: data.random_end_time?.substring(0, 5),
        startDate: data.start_date,
        endDate: data.end_date,
        isActive: data.is_active,
        createdAt: data.created_at
    };
};

export const mapFlashAlertInstanceFromDB = (data: Record<string, any>): FlashAlertInstance => {
    if (!data) return {} as any;
    return {
        id: data.id,
        configId: data.config_id,
        clientId: data.client_id,
        status: data.status as FlashAlertStatus,
        triggeredAt: data.triggered_at,
        deadlineAt: data.deadline_at,
        completedAt: data.completed_at,
        photoUrl: data.photo_url,
        clientComment: data.client_comment,
        dominaComment: data.domina_comment,
        isNotified: data.is_notified,
        createdAt: data.created_at
    };
};

export const normalizeVisibilityPreset = (val?: string): TaskVisibilityPreset => {
    if (!val) return TaskVisibilityPreset.IMMEDIATE;
    if (val === 'Benutzerdefiniertes Datum' || val === 'Benutzerdefiniert' || val.startsWith('Benutzerdefiniert')) {
        return TaskVisibilityPreset.CUSTOM;
    }
    if (val === '24h vor Fälligkeit') return TaskVisibilityPreset.H24_BEFORE;
    if (val === '7 Tage vor Fälligkeit') return TaskVisibilityPreset.D7_BEFORE;
    if (val === 'Sofort sichtbar') return TaskVisibilityPreset.IMMEDIATE;
    return TaskVisibilityPreset.IMMEDIATE;
};

export const mapTemplateFromDB = (data: Record<string, any>): TaskTemplate => {
    const labelData = getJoinedData(data, 'task_labels');
    
    return {
        id: data.id,
        title: data.title,
        description: data.description,
        category: normalizeCategory(data.category),
        type: normalizeType(data.type),
        durationTarget: data.duration_target,
        countTarget: data.count_target,
        intervalConfig: data.interval_config || [],
        startDate: data.start_date,
        endDate: data.end_date,
        deadlineTime: data.deadline_time,
        recurrence: data.recurrence as RecurrenceType,
        recurrenceEndType: data.recurrence_end_type || 'INDEFINITE',
        recurrenceEndDate: data.recurrence_end_date,
        visibilityPreset: normalizeVisibilityPreset(data.visibility_preset),
        customVisibilityDate: data.custom_visibility_date,
        reviewRequired: !!data.review_required,
        isReportRequired: !!data.is_report_required,
        isProofRequired: !!data.is_proof_required,
        attachmentLink: data.attachment_link,
        labelId: data.label_id,
        isMultiDay: data.is_multi_day,
        goddessNotes: data.goddess_notes,
        goddessReminders: data.goddess_reminders || [],
        assignedClientIds: data.assigned_client_ids || [],
        label: labelData ? {
            id: labelData.id,
            userId: labelData.user_id,
            name: labelData.name,
            color: labelData.color
        } : null
    };
};

export const mapConversationFromDB = (data: Record<string, any>, unreadCount: number = 0): Conversation => {
    const clientData = getJoinedData(data, 'clients');
    return {
        id: data.id,
        clientId: data.client_id,
        clientName: clientData?.name || 'Unbekannter Klient',
        clientIsPaused: clientData?.is_paused,
        dominaId: data.domina_id,
        lastMessage: null,
        lastMessageTimestamp: data.updated_at,
        unreadCount,
        isArchived: data.is_archived
    };
};

const retryOperation = async <T>(operation: () => Promise<T>, retries = 3, initialDelay = 1000): Promise<T> => {
    let lastError: any;
    for (let i = 0; i < retries; i++) {
        try {
            return await operation();
        } catch (error: any) {
            lastError = error;
            const err = error as { code?: string; status?: number; message?: string; name?: string };
            if (err.code === 'PGRST205' || err.status === 401 || err.status === 403) break;
            if (err.message?.includes('Failed to fetch') || err.name === 'TypeError' || err.message?.includes('NetworkError')) {
                console.warn(`Netzwerkfehler (Versuch ${i+1}/${retries}): ${err.message}. Prüfe Internet/Ad-Blocker.`);
            }
            if (i < retries - 1) await new Promise(res => setTimeout(res, initialDelay * Math.pow(2, i)));
        }
    }
    throw lastError;
};

const DOMINA_EMAILS = ['cat@fairyrealm.com', 'cat@fairyrule.com', 'catherineisallaround@gmail.com'];

// --- AUTH / USER DETAILS ---
export const getAppUserDetails = async (email: string): Promise<{ role: UserRole | null, clientId?: string, theme?: UserTheme, navigationLayout?: NavigationLayout, telegramChatId?: string, error?: string }> => {
    // Local storage fallback for instant responsiveness
    const normalizedEmail = email.trim().toLowerCase();
    const localThemeKey = `fairyrealm_theme_${normalizedEmail}`;
    const localNavKey = `fairyrealm_nav_layout_${normalizedEmail}`;
    const savedLocalTheme = localStorage.getItem(localThemeKey) as UserTheme | null;
    const savedLocalNavLayout = (localStorage.getItem(localNavKey) as NavigationLayout) || NavigationLayout.SIDEBAR;

    return retryOperation(async () => {
        if (DOMINA_EMAILS.includes(normalizedEmail)) {
            try {
                console.log(`[API] Fetching Domina details for ${normalizedEmail}`);
                const query = supabase.from('domina_status').select('theme, telegram_chat_id').eq('id', 1).maybeSingle();
                const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Query Timeout (domina_status)')), 10000));
                
                let themeFromDb: UserTheme | null = null;
                let telegramIdFromDb: string | undefined = undefined;

                try {
                    const result = (await Promise.race([query, timeout])) as { data: any, error?: any };
                    const isNoRowsError = result.error && (
                        result.error.code === 'PGRST116' || 
                        result.error.message?.includes('0 rows') || 
                        result.error.message?.includes('multiple (or no) rows returned')
                    );

                    if (result.error && !isNoRowsError) {
                        const errMsg = (result.error.message || String(result.error)).toLowerCase();
                        const isNetworkError = errMsg.includes('failed to fetch') || 
                                               errMsg.includes('networkerror') || 
                                               errMsg.includes('typeerror') || 
                                               errMsg.includes('timeout') || 
                                               errMsg.includes('load failed');
                        if (isNetworkError) {
                            console.warn(`[API] Domina status query transient network issue (using local fallback):`, result.error.message || result.error);
                        } else {
                            console.warn(`[API] Domina query warning:`, result.error);
                        }
                        themeFromDb = null;
                    } else if (!result.data || isNoRowsError) {
                        console.log(`[API] Domina status row 1 is missing. Attempting self-healing insertion...`);
                        try {
                            const { data: { user } } = await supabase.auth.getUser();
                            const upsertData: any = { id: 1, status: 'Offline', comment: 'System initialisiert', theme: 'amethyst' };
                            if (user) upsertData.domina_id = user.id;
                            
                            const { data: insertedData, error: insertError } = await supabase.from('domina_status').upsert(upsertData).select().single();
                            if (insertError) {
                                console.warn(`[API] Self-healing insertion warning:`, insertError.message || insertError);
                            } else if (insertedData) {
                                console.log(`[API] Self-healing insertion succeeded:`, insertedData);
                                themeFromDb = insertedData.theme as UserTheme;
                                telegramIdFromDb = insertedData.telegram_chat_id;
                            }
                        } catch (err) {
                            console.warn(`[API] Self-healing insertion threw warning:`, err);
                        }
                    } else {
                        themeFromDb = result.data?.theme as UserTheme;
                        telegramIdFromDb = result.data?.telegram_chat_id;
                        console.log(`[API] Domina details found:`, { themeFromDb, telegramIdFromDb });
                    }
                } catch (e) {
                    console.warn("Theme query failed, using fallback", e);
                }
                
                const finalTheme = themeFromDb || savedLocalTheme || UserTheme.AMETHYST;

                return { role: UserRole.DOMINA, theme: finalTheme, navigationLayout: savedLocalNavLayout, telegramChatId: telegramIdFromDb };
            } catch (e) {
                console.warn("Domina status check failed, using fallback", e);
                return { role: UserRole.DOMINA, theme: savedLocalTheme || UserTheme.AMETHYST, navigationLayout: savedLocalNavLayout };
            }
        }
        
        const query = supabase.from('clients').select('id, is_active, theme, profile, telegram_chat_id').eq('email', normalizedEmail).maybeSingle();
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Query Timeout (clients)')), 30000));
        
        let data: any = null;
        let error: any = null;
        
        try {
            const result = (await Promise.race([query, timeout])) as { data: any, error: any };
            data = result.data;
            error = result.error;
            
            // Handle missing theme column
            if (error && (error.code === 'PGRST204' || error.message?.includes('theme'))) {
                const queryNoTheme = supabase.from('clients').select('id, is_active, profile').eq('email', normalizedEmail).maybeSingle();
                const resultNoTheme = (await Promise.race([queryNoTheme, timeout])) as { data: any, error: any };
                data = resultNoTheme.data;
                error = resultNoTheme.error;
            }
        } catch (e) {
            error = e;
        }
        
        if (error) throw error;

        if (data) {
            if (!data.is_active) return { role: null, error: 'Konto deaktiviert.' };
            
            let theme = data.theme as UserTheme;
            const navLayout = data.profile?.navigationLayout as NavigationLayout;
            if (!theme && data.profile?.theme) {
                theme = data.profile.theme as UserTheme;
            }
            
            const finalTheme = theme || savedLocalTheme || UserTheme.AMETHYST;
            const finalNavLayout = navLayout || savedLocalNavLayout;

            // Sync local storage
            if (theme && theme !== savedLocalTheme) {
                localStorage.setItem(localThemeKey, theme);
            }
            if (navLayout && navLayout !== savedLocalNavLayout) {
                localStorage.setItem(localNavKey, navLayout);
            }

            return { 
                role: UserRole.SUB, 
                clientId: data.id, 
                theme: finalTheme,
                navigationLayout: finalNavLayout,
                telegramChatId: data.telegram_chat_id
            };
        }
        return { role: null, error: `Profil zu ${normalizedEmail} nicht gefunden.` };
    });
};

export const updateNavigationLayout = async (layout: NavigationLayout, role: UserRole, clientId?: string): Promise<void> => {
    // 1. Update local storage immediately for UI responsiveness
    const { data: authData } = await supabase.auth.getUser();
    if (authData.user?.email) {
        localStorage.setItem(`fairyrealm_nav_layout_${authData.user.email.toLowerCase()}`, layout);
    }

    // 2. Persist to database if possible
    await retryOperation(async () => {
        if (role === UserRole.DOMINA) {
            const { error } = await supabase.from('domina_status').update({ navigation_layout: layout }).eq('id', 1);
            if (error && (error.code === 'PGRST204' || error.message?.includes('navigation_layout'))) {
                return;
            }
        } else if (clientId) {
            const { data: client } = await supabase.from('clients').select('profile').eq('id', clientId).maybeSingle();
            const profile = (client as any)?.profile || {};
            
            const { error } = await supabase.from('clients').update({ 
                profile: { ...profile, navigationLayout: layout }
            }).eq('id', clientId);
            
            if (error && (error.code === 'PGRST204' || error.message?.includes('profile'))) {
                return;
            }
        }
    });
};

export const updateUserTheme = async (theme: UserTheme, role: UserRole, clientId?: string): Promise<void> => {
    // 1. Update local storage immediately for UI responsiveness
    const { data: authData } = await supabase.auth.getUser();
    if (authData.user?.email) {
        localStorage.setItem(`fairyrealm_theme_${authData.user.email.toLowerCase()}`, theme);
    }

    // 2. Persist to database
    await retryOperation(async () => {
        if (role === UserRole.DOMINA) {
            const { error } = await supabase.from('domina_status').update({ theme }).eq('id', 1);
            if (error) {
                if (error.code === 'PGRST204' || error.message?.includes('theme')) {
                    // Spalte fehlt in DB, nur lokal speichern
                    return;
                }
                throw error;
            }
        } else if (clientId) {
            // First get the profile to preserve it
            const { data: client } = await supabase.from('clients').select('profile').eq('id', clientId).maybeSingle();
            const profile = (client as any)?.profile || {};
            
            const { error } = await supabase.from('clients').update({ 
                theme,
                profile: { ...profile, theme }
            }).eq('id', clientId);
            
            if (error) {
                if (error.code === 'PGRST204' || error.message?.includes('theme')) {
                    // Spalte fehlt in DB, nur lokal speichern
                    return;
                }
                throw error;
            }
        }
    });
};

export const updateTelegramChatId = async (id: string, role: UserRole, telegramChatId: string): Promise<void> => {
    await retryOperation(async () => {
        // 1. Always sync to the unified mapping table for reliable notification routing
        try {
            console.log(`[API] Syncing to unified mapping: user=${id}, chat=${telegramChatId}`);
            const { error: mappingErr } = await supabase.from('telegram_mappings').upsert({
                user_id: id,
                chat_id: telegramChatId,
                updated_at: new Date().toISOString()
            }, { onConflict: 'user_id' });
            
            if (mappingErr) {
                console.warn("[API] Mapping sync skip:", mappingErr.message);
                // Non-critical: allow app to continue if table doesn't exist
            }
        } catch (e) {
            console.warn("[API] Mapping sync catch:", e);
        }

        // 2. Original legacy sync logic
        if (role === UserRole.DOMINA) {
            console.log(`[API] Updating Domina Telegram ID to ${telegramChatId}`);
            const { error } = await supabase.from('domina_status').upsert({ 
                id: 1, 
                telegram_chat_id: telegramChatId,
                domina_id: id,
                status: 'offline' 
            }, { onConflict: 'id' });
            if (error) throw error;
        } else {
            console.log(`[API] Updating Client ${id} Telegram ID to ${telegramChatId}`);
            const { error } = await supabase.from('clients').update({ telegram_chat_id: telegramChatId }).eq('id', id);
            if (error) throw error;
        }
    });
};

export const registerDomina = async (id: string) => {
    try {
        const { error } = await supabase.from('domina_status').update({ domina_id: id }).eq('id', 1);
        if (error) throw error;
        // Also update all conversations that don't have a domina_id yet
        await supabase.from('conversations').update({ domina_id: id }).is('domina_id', null);
    } catch (e) {
        console.warn("Failed to register Domina ID", e);
    }
};

export const getDominaId = async (): Promise<string | null> => {
    try {
        const { data: status } = await supabase.from('domina_status').select('domina_id').eq('id', 1).maybeSingle();
        return status?.domina_id || null;
    } catch (e) {
        console.warn("Failed to get Domina ID", e);
        return null;
    }
};

export const updateLastLogin = async (clientId: string) => {
    await retryOperation(async () => {
        const { error } = await supabase.from('clients').update({ last_login: new Date().toISOString() }).eq('id', clientId);
        if (error) throw error;
    });
};

// --- WEEKLY PLAN SNAPSHOTS (RESILIENT) ---

export const getSealedWeek = async (clientId: string, weekKey: string) => {
    return retryOperation(async () => {
        try {
            const { data, error: fetchError = null } = await supabase
                .from('sealed_weeks')
                .select('*')
                .eq('client_id', clientId)
                .eq('week_key', weekKey)
                .maybeSingle(); 
            if (fetchError) throw fetchError;
            return data;
        } catch (e: any) {
            if (e.code === 'PGRST205' || e.message?.includes('sealed_weeks')) return null;
            throw e;
        }
    });
};

export const syncWeeklyPlanToDailyPlans = async (clientId: string, weekKey: string, snapshotData: any) => {
    try {
        const mondayDate = getWeekStartDateFromKey(weekKey);
        for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
            const targetDate = new Date(mondayDate);
            targetDate.setDate(targetDate.getDate() + dayIdx);
            const dateStr = targetDate.toISOString().split('T')[0];

            const { data: existing } = await supabase.from('daily_plans').select('*').eq('client_id', clientId).eq('date', dateStr).maybeSingle();
            if (!existing || !existing.is_sealed) {
                const dayItems = (snapshotData.plan || []).filter((item: any) => item.day === dayIdx);
                const workTime = (snapshotData.workTimes || []).find((wt: any) => wt.day === dayIdx);
                const isFree = (snapshotData.freeDayIndices || []).includes(dayIdx);
                const checklist = snapshotData.dayChecklists?.[dayIdx] || [];

                await supabase.from('daily_plans').upsert({
                    client_id: clientId,
                    date: dateStr,
                    items: dayItems,
                    checklist: checklist,
                    work_start: workTime?.start || null,
                    work_end: workTime?.end || null,
                    is_free_day: isFree,
                    is_sick_day: existing ? existing.is_sick_day : false,
                    is_sealed: false
                }, { onConflict: 'client_id,date' });
            }
        }
    } catch (err) {
        console.error("Failed to sync weekly plan to daily plans:", err);
    }
};

export const sealWeek = async (clientId: string, weekKey: string, snapshotData: any) => {
    return retryOperation(async () => {
        const { error: upsertError } = await supabase
            .from('sealed_weeks')
            .upsert({
                client_id: clientId,
                week_key: weekKey,
                data: snapshotData,
                sealed_at: new Date().toISOString()
            }, { onConflict: 'client_id,week_key' });
        
        if (upsertError) throw upsertError;
        
        const { data: client, error: fetchError } = await supabase.from('clients').select('profile').eq('id', clientId).maybeSingle();
        if (fetchError) throw fetchError;
        
        const profile = client?.profile || {};
        const sealedWeeks = { ...(profile.sealedWeeks || {}) };
        sealedWeeks[weekKey] = new Date().toISOString();
        
        const weekData = { ...(profile.weekData || {}) };
        weekData[weekKey] = snapshotData;

        const { error: updateError = null } = await supabase.from('clients').update({ profile: { ...profile, sealedWeeks, weekData } }).eq('id', clientId);
        if (updateError) throw updateError;

        await syncWeeklyPlanToDailyPlans(clientId, weekKey, snapshotData);
    });
};

export const unsealWeek = async (clientId: string, weekKey: string) => {
    return retryOperation(async () => {
        const { error: delError } = await supabase
            .from('sealed_weeks')
            .delete()
            .eq('client_id', clientId)
            .eq('week_key', weekKey);
        if (delError) throw delError;

        const { data: client, error: fetchError } = await supabase.from('clients').select('profile').eq('id', clientId).maybeSingle();
        if (fetchError) throw fetchError;

        const profile = client?.profile || {};
        const sealedWeeks = { ...(profile.sealedWeeks || {}) };
        delete sealedWeeks[weekKey];
        
        const { error: updateError = null } = await supabase.from('clients').update({ profile: { ...profile, sealedWeeks } }).eq('id', clientId);
        if (updateError) throw updateError;
    });
};

export const getAllSealedWeeksInRange = async (weekKeys: string[]) => {
    return retryOperation(async () => {
        try {
            const { data, error } = await supabase
                .from('sealed_weeks')
                .select('*')
                .in('week_key', weekKeys);
            if (error) throw error;
            return data || [];
        } catch (e: any) {
            if (e.code === 'PGRST205' || e.message?.includes('sealed_weeks')) return [];
            throw e;
        }
    });
};

// --- DAILY PLANS ---
export const getDailyPlan = async (clientId: string, date: string): Promise<DailyPlan | null> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('daily_plans').select('*').eq('client_id', clientId).eq('date', date).maybeSingle();
        if (error) throw error;
        
        const existingPlan = data ? mapDailyPlanFromDB(data) : null;
        if (existingPlan && (existingPlan.isSealed || (existingPlan.items && existingPlan.items.length > 0))) {
            return existingPlan;
        }

        const parts = date.split('-');
        const dateObj = parts.length === 3 ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])) : new Date(date);
        const weekdayIndex = (dateObj.getDay() + 6) % 7;
        const weekInfo = getWeekInfo(date);
        const weekKey = weekInfo.key;

        let activeSnapshot: any = null;
        try {
            const { data: sealedData } = await supabase.from('sealed_weeks').select('data').eq('client_id', clientId).eq('week_key', weekKey).maybeSingle();
            if (sealedData?.data) {
                activeSnapshot = sealedData.data;
            }
        } catch (e) {
            // ignore
        }

        if (!activeSnapshot) {
            const client = await getClientById(clientId);
            if (client?.profile?.weekData?.[weekKey]) {
                activeSnapshot = client.profile.weekData[weekKey];
            } else if (client?.profile) {
                activeSnapshot = {
                    plan: client.profile.weeklyPlan || [],
                    workTimes: client.profile.weeklyWorkTimes || [],
                    freeDayIndices: client.profile.freeDayIndices || [],
                    dayChecklists: client.profile.dayChecklists || {}
                };
            }
        }

        if (activeSnapshot) {
            const dayItems = (activeSnapshot.plan || []).filter((item: any) => item.day === weekdayIndex);
            const workTime = (activeSnapshot.workTimes || []).find((wt: any) => wt.day === weekdayIndex);
            const isFree = (activeSnapshot.freeDayIndices || []).includes(weekdayIndex);
            const checklist = activeSnapshot.dayChecklists?.[weekdayIndex] || [];

            return {
                id: existingPlan?.id || `auto-${clientId}-${date}`,
                clientId,
                date,
                items: dayItems,
                checklist: checklist,
                workStart: workTime?.start || '08:00',
                workEnd: workTime?.end || '17:00',
                isFreeDay: existingPlan ? existingPlan.isFreeDay : isFree,
                isSickDay: existingPlan ? existingPlan.isSickDay : false,
                isSealed: existingPlan ? existingPlan.isSealed : false,
                sealedAt: existingPlan?.sealedAt
            };
        }

        return existingPlan;
    });
};

export const getDailyPlansInRange = async (startDate: string, endDate: string): Promise<DailyPlan[]> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase
            .from('daily_plans')
            .select('*')
            .gte('date', startDate)
            .lte('date', endDate);
        if (error) throw error;
        return (data || []).map(mapDailyPlanFromDB);
    });
};

export const upsertDailyPlan = async (plan: Omit<DailyPlan, 'id'>): Promise<DailyPlan> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase
            .from('daily_plans')
            .upsert({
                client_id: plan.clientId,
                date: plan.date,
                items: plan.items,
                checklist: plan.checklist || [],
                work_start: plan.workStart || null,
                work_end: plan.workEnd || null,
                wake_up_time: plan.wakeUpTime || null,
                bed_time: plan.bedTime,
                is_free_day: !!plan.isFreeDay,
                is_sick_day: !!plan.isSickDay,
                is_sealed: !!plan.isSealed,
                sealed_at: plan.isSealed ? (plan.sealedAt || new Date().toISOString()) : null
            }, { onConflict: 'client_id,date' })
            .select()
            .single();
        if (error) throw error;
        return mapDailyPlanFromDB(data);
    });
};

export const unsealDailyPlan = async (clientId: string, date: string): Promise<void> => {
    return retryOperation(async () => {
        const { error } = await supabase.from('daily_plans').update({ is_sealed: false, sealed_at: null }).eq('client_id', clientId).eq('date', date);
        if (error) throw error;
    });
};

export const getDailyPlansForDate = async (date: string): Promise<DailyPlan[]> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('daily_plans').select('*').eq('date', date);
        if (error) throw error;
        return (data || []).map(mapDailyPlanFromDB);
    });
};

// --- CLIENTS ---
export const getClients = async (): Promise<Client[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('clients').select('*').order('name');
        if (error) throw error;
        return (data || []).map(mapClientFromDB);
    });
};

export const getClientById = async (id: string): Promise<Client | null> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('clients').select('*').eq('id', id).maybeSingle();
        if (error) return null;
        return data ? mapClientFromDB(data) : null;
    });
};

export const updateClient = async (id: string, updates: Partial<Client>): Promise<Client> => {
    return retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
        if (updates.isPaused !== undefined) dbUpdates.is_paused = updates.isPaused;
        if (updates.email !== undefined) dbUpdates.email = updates.email;
        if (updates.birthDate !== undefined) dbUpdates.birth_date = updates.birthDate;
        if (updates.entryDate !== undefined) dbUpdates.entry_date = updates.entryDate;
        if (updates.preferences !== undefined) dbUpdates.preferences = updates.preferences;
        if (updates.taboos !== undefined) dbUpdates.taboos = updates.taboos;
        if (updates.rulebookId !== undefined) dbUpdates.rulebook_id = updates.rulebookId;
        if (updates.reportTemplateId !== undefined) dbUpdates.report_template_id = updates.reportTemplateId;
        if (updates.goddessNotes !== undefined) dbUpdates.goddess_notes = updates.goddessNotes;
        if (updates.contact !== undefined) dbUpdates.contact = updates.contact;
        if (updates.financials !== undefined) dbUpdates.financials = updates.financials;
        
        // Handle profile, timezone, periodEndDate, rulebookIds, and reportTemplateIds
        if (updates.timezone !== undefined || updates.rulebookIds !== undefined || updates.reportTemplateIds !== undefined || updates.periodEndDate !== undefined || updates.profile !== undefined) {
            const existingClient = await getClientById(id);
            const existingProfile = updates.profile || existingClient?.profile || { about: '' };
            const mergedProfile = {
                ...existingProfile,
                ...(updates.profile || {})
            };
            if (updates.timezone !== undefined) {
                mergedProfile.timezone = updates.timezone;
            }
            if (updates.rulebookIds !== undefined) {
                mergedProfile.rulebookIds = updates.rulebookIds;
                if (dbUpdates.rulebook_id === undefined) {
                    dbUpdates.rulebook_id = updates.rulebookIds[0] || null;
                }
            }
            if (updates.reportTemplateIds !== undefined) {
                mergedProfile.reportTemplateIds = updates.reportTemplateIds;
                if (dbUpdates.report_template_id === undefined) {
                    dbUpdates.report_template_id = updates.reportTemplateIds[0] || null;
                }
            }
            if (updates.periodEndDate !== undefined) {
                mergedProfile.periodEndDate = updates.periodEndDate || undefined;
            }
            dbUpdates.profile = mergedProfile;
        }

        if (updates.contract !== undefined) dbUpdates.contract = updates.contract;
        if (updates.messages !== undefined) dbUpdates.messages = updates.messages;
        if (updates.theme !== undefined) dbUpdates.theme = updates.theme;
        if (updates.telegramChatId !== undefined) dbUpdates.telegram_chat_id = updates.telegramChatId;
        const { data, error = null } = await supabase.from('clients').update(dbUpdates).eq('id', id).select().single();
        if (error) throw error;

        // CLEANUP: If client is deactivated, remove from all task assignments and delete pending tasks
        if (updates.isActive === false) {
            try {
                // Delete all pending tasks for this client
                await supabase.from('task_instances')
                    .delete()
                    .eq('client_id', id)
                    .eq('status', 'PENDING');

                // Remove client from all task templates
                const { data: templatesToUpdate } = await supabase.from('task_templates')
                    .select('id, assigned_client_ids')
                    .contains('assigned_client_ids', [id]);
                
                if (templatesToUpdate && templatesToUpdate.length > 0) {
                    for (const tmpl of templatesToUpdate) {
                        const newAssigned = (tmpl.assigned_client_ids || []).filter((cid: string) => cid !== id);
                        await supabase.from('task_templates')
                            .update({ assigned_client_ids: newAssigned })
                            .eq('id', tmpl.id);
                    }
                }
            } catch (cleanupError) {
                console.error("Cleanup for deactivated client failed:", cleanupError);
                // We don't throw here to ensure the client update itself is considered successful
            }
        }

        return mapClientFromDB(data);
    });
};

export const updateClientPeriodEndDate = async (id: string, periodEndDate: string | null): Promise<Client> => {
    return retryOperation(async () => {
        const existing = await getClientById(id);
        const currentProfile = existing?.profile || { about: '' };
        const updatedProfile = {
            ...currentProfile,
            periodEndDate: periodEndDate || undefined
        };
        const dbUpdates: Record<string, unknown> = {
            profile: updatedProfile
        };
        const { data, error = null } = await supabase.from('clients').update(dbUpdates).eq('id', id).select().single();
        if (error) throw error;
        return mapClientFromDB(data);
    });
};

export const addClient = async (client: Omit<Client, 'id' | 'isActive'>): Promise<Client> => {
    return retryOperation(async () => {
        const profileToSave = {
            ...client.profile,
            periodEndDate: client.periodEndDate || client.profile?.periodEndDate || undefined
        };
        const { data, error = null } = await supabase.from('clients').insert({
            name: client.name,
            email: client.email,
            birth_date: client.birthDate,
            entry_date: client.entryDate,
            is_active: true,
            is_paused: client.isPaused,
            preferences: client.preferences,
            taboos: client.taboos,
            contact: client.contact,
            financials: client.financials,
            profile: profileToSave,
            goddess_notes: client.goddessNotes,
            rulebook_id: client.rulebookId,
            report_template_id: client.reportTemplateId,
            theme: client.theme,
            telegram_chat_id: client.telegramChatId,
            contract: client.contract,
            messages: client.messages
        }).select().single();
        if (error) throw error;
        return mapClientFromDB(data);
    });
};

export const deleteClient = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        // Fetch client name to preserve with payments
        let clientName = 'Gelöschter Klient';
        try {
            const { data: clientData } = await supabase.from('clients').select('name').eq('id', id).maybeSingle();
            if (clientData?.name) {
                clientName = clientData.name;
            }
        } catch (e) {
            console.warn('Could not fetch client name before deletion:', e);
        }

        // 1. Conversations & Messages
        try {
            const { data: convos } = await supabase.from('conversations').select('id').eq('client_id', id);
            if (convos && convos.length > 0) {
                const convoIds = convos.map(c => c.id);
                await supabase.from('messages').delete().in('conversation_id', convoIds);
                await supabase.from('conversations').delete().eq('client_id', id);
            }
        } catch (e) {
            console.warn('Error deleting client conversations/messages:', e);
        }

        // 2. Task Instances
        try {
            await supabase.from('task_instances').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client task_instances:', e);
        }

        // 3. Daily Plans & Sealed Weeks
        try {
            await supabase.from('daily_plans').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client daily_plans:', e);
        }
        try {
            await supabase.from('sealed_weeks').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client sealed_weeks:', e);
        }

        // 4. Daily Checklists
        try {
            await supabase.from('daily_checklists').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client daily_checklists:', e);
        }

        // 5. Diary Entries
        try {
            await supabase.from('diary_entries').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client diary_entries:', e);
        }

        // 6. Report Submissions
        try {
            await supabase.from('report_submissions').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client report_submissions:', e);
        }

        // 7. Preserve Payments (Do NOT delete payments - retain history with client name)
        try {
            const { data: clientPayments } = await supabase.from('payments').select('id, description').eq('client_id', id);
            if (clientPayments && clientPayments.length > 0) {
                for (const p of clientPayments) {
                    const originalDesc = p.description || '';
                    const newDesc = originalDesc.startsWith('[Klient:')
                        ? originalDesc
                        : `[Klient: ${clientName}] ${originalDesc}`.trim();
                    
                    // Unlink client_id to null so foreign key constraints on clients table do not block deletion
                    const { error: unlinkError } = await supabase.from('payments')
                        .update({ description: newDesc, client_id: null })
                        .eq('id', p.id);
                    
                    if (unlinkError) {
                        // Fallback: If client_id cannot be null, at least store the name in description
                        await supabase.from('payments')
                            .update({ description: newDesc })
                            .eq('id', p.id);
                    }
                }
            }
        } catch (e) {
            console.warn('Error preserving client payments:', e);
        }

        // 8. Behavioral Feedback
        try {
            await supabase.from('behavioral_feedback').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client behavioral_feedback:', e);
        }

        // 9. Emotion Logs
        try {
            await supabase.from('emotion_logs').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client emotion_logs:', e);
        }

        // 10. App Notifications
        try {
            await supabase.from('app_notifications').delete().eq('user_id', id);
        } catch (e) {
            console.warn('Error deleting client app_notifications:', e);
        }

        // 11. Telegram Mappings
        try {
            await supabase.from('telegram_mappings').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client telegram_mappings:', e);
        }

        // 12. Control Configs, Goals, Weekly Checks, and Alerts
        try {
            const { data: configs } = await supabase.from('control_configs').select('id').eq('client_id', id);
            if (configs && configs.length > 0) {
                const configIds = configs.map(c => c.id);
                await supabase.from('control_goals').delete().in('config_id', configIds);
                await supabase.from('control_weekly_checks').delete().in('config_id', configIds);
            }
            await supabase.from('control_configs').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client control_configs:', e);
        }

        try {
            await supabase.from('control_alerts').delete().eq('client_id', id);
            const { data: alertConfigs } = await supabase.from('control_alert_configs').select('id').eq('client_id', id);
            if (alertConfigs && alertConfigs.length > 0) {
                const alertConfigIds = alertConfigs.map(c => c.id);
                await supabase.from('control_alerts').delete().in('config_id', alertConfigIds);
            }
            await supabase.from('control_alert_configs').delete().eq('client_id', id);
        } catch (e) {
            console.warn('Error deleting client control_alert_configs:', e);
        }

        // 13. Task Templates cleanup
        try {
            const { data: templates } = await supabase.from('task_templates').select('id, assigned_client_ids');
            if (templates) {
                for (const tmpl of templates) {
                    if (tmpl.assigned_client_ids && tmpl.assigned_client_ids.includes(id)) {
                        const updated = tmpl.assigned_client_ids.filter((cid: string) => cid !== id);
                        await supabase.from('task_templates').update({ assigned_client_ids: updated }).eq('id', tmpl.id);
                    }
                }
            }
        } catch (e) {
            console.warn('Error cleaning up task_templates assignedClientIds:', e);
        }

        // 14. Default Checklist Items cleanup
        try {
            const { data: items } = await supabase.from('default_checklist_items').select('id, assigned_client_ids');
            if (items) {
                for (const item of items) {
                    if (item.assigned_client_ids && item.assigned_client_ids.includes(id)) {
                        const updated = item.assigned_client_ids.filter((cid: string) => cid !== id);
                        await supabase.from('default_checklist_items').update({ assigned_client_ids: updated }).eq('id', item.id);
                    }
                }
            }
        } catch (e) {
            console.warn('Error cleaning up default_checklist_items assignedClientIds:', e);
        }

        // 15. Media folders & files cleanup
        try {
            const { data: folders } = await supabase.from('media_folders').select('id, assigned_client_ids');
            if (folders) {
                for (const folder of folders) {
                    if (folder.assigned_client_ids && folder.assigned_client_ids.includes(id)) {
                        const updated = folder.assigned_client_ids.filter((cid: string) => cid !== id);
                        await supabase.from('media_folders').update({ assigned_client_ids: updated.length ? updated : null }).eq('id', folder.id);
                    }
                }
            }
            const { data: files } = await supabase.from('media_files').select('id, assigned_client_ids');
            if (files) {
                for (const file of files) {
                    if (file.assigned_client_ids && file.assigned_client_ids.includes(id)) {
                        const updated = file.assigned_client_ids.filter((cid: string) => cid !== id);
                        await supabase.from('media_files').update({ assigned_client_ids: updated.length ? updated : null }).eq('id', file.id);
                    }
                }
            }
        } catch (e) {
            console.warn('Error cleaning up media assignedClientIds:', e);
        }

        // 16. Finally delete the client record
        const { error = null } = await supabase.from('clients').delete().eq('id', id);
        if (error) throw error;
    });
};

export const pauseClient = async (clientId: string): Promise<void> => {
    return retryOperation(async () => {
        const { error: clientError = null } = await supabase.from('clients').update({ is_paused: true }).eq('id', clientId);
        if (clientError) throw clientError;

        const now = new Date().toISOString();
        const { error: tasksError = null } = await supabase.from('task_instances')
            .delete()
            .eq('client_id', clientId)
            .gte('end_date_time', now)
            .in('status', [TaskStatus.PENDING, TaskStatus.IN_PROGRESS]);
        if (tasksError) throw tasksError;
    });
};

// --- TASKS & TEMPLATES ---

export const getTaskTemplates = async (): Promise<TaskTemplate[]> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('task_templates').select('*, task_labels(*)').order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []).map(mapTemplateFromDB);
    });
};

export const addTaskTemplate = async (template: Omit<TaskTemplate, 'id' | 'label'>): Promise<TaskTemplate> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('task_templates').insert({
            title: template.title,
            description: template.description,
            category: template.category,
            type: template.type,
            duration_target: template.durationTarget,
            count_target: template.countTarget,
            interval_config: template.intervalConfig || [],
            start_date: template.startDate,
            end_date: template.endDate,
            deadline_time: template.deadlineTime,
            recurrence: template.recurrence,
            recurrence_end_type: template.recurrenceEndType || 'INDEFINITE',
            recurrence_end_date: template.recurrenceEndDate,
            visibility_preset: template.visibilityPreset || TaskVisibilityPreset.IMMEDIATE,
            custom_visibility_date: template.customVisibilityDate,
            review_required: !!template.reviewRequired,
            is_report_required: !!template.isReportRequired,
            is_proof_required: !!template.isProofRequired,
            attachment_link: template.attachmentLink,
            label_id: template.labelId,
            is_multi_day: !!template.isMultiDay,
            assigned_client_ids: template.assignedClientIds || []
        }).select('*, task_labels(*)').single();
        if (error) throw error;
        clearGeneratedDaysCache();
        return mapTemplateFromDB(data);
    });
};

export const updateTaskTemplate = async (id: string, updates: Partial<TaskTemplate>): Promise<TaskTemplate> => {
    return retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.category !== undefined) dbUpdates.category = updates.category;
        if (updates.type !== undefined) dbUpdates.type = updates.type;
        if (updates.durationTarget !== undefined) dbUpdates.duration_target = updates.durationTarget;
        if (updates.countTarget !== undefined) dbUpdates.count_target = updates.countTarget;
        if (updates.intervalConfig !== undefined) dbUpdates.interval_config = updates.intervalConfig;
        if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
        if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
        if (updates.deadlineTime !== undefined) dbUpdates.deadline_time = updates.deadlineTime;
        if (updates.recurrence !== undefined) dbUpdates.recurrence = updates.recurrence;
        if (updates.recurrenceEndType !== undefined) dbUpdates.recurrence_end_type = updates.recurrenceEndType;
        if (updates.recurrenceEndDate !== undefined) dbUpdates.recurrence_end_date = updates.recurrenceEndDate;
        if (updates.visibilityPreset !== undefined) dbUpdates.visibility_preset = updates.visibilityPreset;
        if (updates.customVisibilityDate !== undefined) dbUpdates.custom_visibility_date = updates.customVisibilityDate;
        if (updates.reviewRequired !== undefined) dbUpdates.review_required = updates.reviewRequired;
        if (updates.isReportRequired !== undefined) dbUpdates.is_report_required = updates.isReportRequired;
        if (updates.isProofRequired !== undefined) dbUpdates.is_proof_required = updates.isProofRequired;
        if (updates.attachmentLink !== undefined) dbUpdates.attachment_link = updates.attachmentLink;
        if (updates.labelId !== undefined) dbUpdates.label_id = updates.labelId;
        if (updates.isMultiDay !== undefined) dbUpdates.is_multi_day = updates.isMultiDay;
        if (updates.assignedClientIds !== undefined) dbUpdates.assigned_client_ids = updates.assignedClientIds;

        const { data, error = null } = await supabase.from('task_templates').update(dbUpdates).eq('id', id).select('*, task_labels(*)').single();
        if (error) throw error;
        clearGeneratedDaysCache();
        return mapTemplateFromDB(data);
    });
};

export const deleteTaskTemplate = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('task_templates').delete().eq('id', id);
        if (error) throw error;
        clearGeneratedDaysCache();
    });
};

const toLocalDateStr = (date: Date) => {
    const yr = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const da = String(date.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
};

export const assignTaskTemplate = async (
    templateId: string, 
    clientIds: string[], 
    startDate: string, 
    deadlineTime: string, 
    visibilityPreset: TaskVisibilityPreset, 
    endDate?: string | null,
    recurrence?: RecurrenceType | null,
    customVisibilityDate?: string | null
): Promise<void> => {
    return retryOperation(async () => {
        const { data: templateData, error: fetchError } = await supabase.from('task_templates').select('*').eq('id', templateId).single();
        if (fetchError) throw fetchError;
        const template = mapTemplateFromDB(templateData);

        // Fetch CURRENT active clients to ensure we don't assign to inactive ones
        const { data: activeClientsData } = await supabase.from('clients')
            .select('id')
            .eq('is_active', true)
            .eq('is_paused', false);
        
        const activeClientIds = new Set(activeClientsData?.map(c => c.id) || []);
        const filteredInputIds = clientIds.filter(id => activeClientIds.has(id));

        if (filteredInputIds.length === 0) {
            return;
        }

        // Preserve all previously assigned clients + add newly checked ones
        const previouslyAssigned = template.assignedClientIds || [];
        const mergedClients = Array.from(new Set([...previouslyAssigned, ...filteredInputIds]));
        
        const now = new Date();
        const todayStr = toLocalDateStr(now);
        const validStartDate = (startDate && startDate.trim()) || todayStr;

        // Recurrence resolution:
        // If recurrence parameter is explicitly provided (including null for "Einmalig"), respect it!
        // Only if undefined, fall back to template.recurrence.
        const effectiveRecurrence: RecurrenceType | null = recurrence !== undefined
            ? (recurrence || null)
            : (template.recurrence || (template.category === TaskCategory.RITUAL ? RecurrenceType.DAILY : null));

        // Sanitize endDate:
        let validEndDate: string | undefined = undefined;
        if (endDate && endDate.trim()) {
            validEndDate = endDate.trim();
            if (validEndDate < validStartDate) {
                validEndDate = validStartDate;
            }
        }

        // Recurrence end date resolution:
        let recurrenceEndDate: string | null = null;
        if (effectiveRecurrence) {
            if (validEndDate && validEndDate >= validStartDate) {
                recurrenceEndDate = validEndDate;
            } else if (template.recurrenceEndDate && template.recurrenceEndDate >= validStartDate) {
                recurrenceEndDate = template.recurrenceEndDate;
            }
        }

        await updateTaskTemplate(templateId, { 
            assignedClientIds: mergedClients,
            recurrence: effectiveRecurrence || undefined,
            recurrenceEndType: recurrenceEndDate ? 'DATE' : (effectiveRecurrence ? 'INDEFINITE' : undefined),
            recurrenceEndDate: recurrenceEndDate || null,
            startDate: validStartDate,
            endDate: effectiveRecurrence ? undefined : (validEndDate || validStartDate),
            deadlineTime: deadlineTime || template.deadlineTime,
            visibilityPreset: visibilityPreset || template.visibilityPreset,
            customVisibilityDate: customVisibilityDate !== undefined ? (customVisibilityDate || undefined) : template.customVisibilityDate
        });

        // Clear lock cache so daily rituals generate cleanly for newly assigned targets
        generatedDaysCache.clear();

        const isHandledByRitualGen = (effectiveRecurrence !== null || template.category === TaskCategory.RITUAL);

        if (isHandledByRitualGen) {
            // RITUALS / RECURRING TASKS: Strictly generate on the CURRENT day (today), never in advance!
            // Future days are generated at midnight when their respective day arrives, up until recurrenceEndDate.
            const todayStr = toLocalDateStr(new Date());
            const recEnd = recurrenceEndDate || validEndDate;
            const isTodayInRange = todayStr >= validStartDate && (!recEnd || todayStr <= recEnd);

            if (isTodayInRange) {
                for (const cid of filteredInputIds) {
                    if (!activeClientIds.has(cid)) continue;
                    try {
                        await autoGenerateDailyRituals(cid, todayStr);
                    } catch (e) {
                        console.error(`Ritual gen failed for ${cid} on ${todayStr}`, e);
                    }
                }
            }
        } else {
            // One-time assignment: strictly use the specified validEndDate, or validStartDate.
            // NEVER use an old template.endDate from the past!
            let endDay = validEndDate || validStartDate;
            if (endDay < validStartDate) {
                endDay = validStartDate;
            }
            
            const instancesToCreate: any[] = [];
            const seriesId = crypto.randomUUID();

            const dTime = deadlineTime || template.deadlineTime || '23:59';
            const formattedDTime = dTime.length === 5 ? `${dTime}:00` : dTime;
            const startDateTime = new Date(`${validStartDate}T00:00:00`);
            const endDateTime = new Date(`${endDay}T${formattedDTime}`);

            // Safety guarantee: endDateTime must never be before startDateTime
            if (endDateTime.getTime() < startDateTime.getTime()) {
                endDateTime.setTime(startDateTime.getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000);
            }

            const getVisibilityDate = (targetEndDateTime: Date, preset: TaskVisibilityPreset, customDateStr?: string) => {
                if (preset === TaskVisibilityPreset.CUSTOM && customDateStr) {
                    const parsed = new Date(customDateStr);
                    if (!isNaN(parsed.getTime())) return parsed;
                }
                const vDate = new Date(targetEndDateTime);
                if (preset === TaskVisibilityPreset.IMMEDIATE) return now;
                if (preset === TaskVisibilityPreset.H24_BEFORE) vDate.setHours(vDate.getHours() - 24);
                else if (preset === TaskVisibilityPreset.D7_BEFORE) vDate.setDate(vDate.getDate() - 7);
                return vDate;
            };

            const visibilityDate = getVisibilityDate(endDateTime, visibilityPreset, customVisibilityDate || template.customVisibilityDate);

            const { data: existingInstances } = await supabase.from('task_instances')
                .select('client_id, template_id, status')
                .in('client_id', filteredInputIds)
                .eq('template_id', templateId)
                .gte('end_date_time', startDateTime.toISOString())
                .lte('end_date_time', endDateTime.toISOString());
            
            const existingPendingTemplateIds = new Set(
                (existingInstances || [])
                    .filter(i => i.status === TaskStatus.PENDING || i.status === TaskStatus.IN_PROGRESS)
                    .map(i => `${i.client_id}:${i.template_id}`)
            );

            filteredInputIds.forEach(clientId => {
                const templateKey = `${clientId}:${templateId}`;
                if (existingPendingTemplateIds.has(templateKey)) return;

                instancesToCreate.push({
                    client_id: clientId,
                    template_id: templateId,
                    series_id: seriesId,
                    title: template.title,
                    description: template.description,
                    category: template.category,
                    type: template.type,
                    status: TaskStatus.PENDING,
                    start_date_time: startDateTime.toISOString(),
                    end_date_time: endDateTime.toISOString(),
                    visibility_date: visibilityDate.toISOString(),
                    review_required: template.reviewRequired,
                    is_report_required: template.isReportRequired,
                    is_proof_required: template.isProofRequired,
                    duration_target: template.durationTarget,
                    count_target: template.countTarget,
                    interval_config: template.intervalConfig || [],
                    attachment_link: template.attachmentLink,
                    goddess_notes: template.goddessNotes,
                    goddess_reminders: template.goddessReminders || [],
                    label_id: template.labelId,
                    is_multi_day: !!template.isMultiDay || (validStartDate !== endDay),
                    comments: [],
                    created_at: now.toISOString(),
                    updated_at: now.toISOString()
                });
            });

            if (instancesToCreate.length > 0) {
                const { error: insertError } = await supabase.from('task_instances').insert(instancesToCreate);
                if (insertError) throw insertError;
            }
        }

        // Notification Trigger
        for (const clientId of filteredInputIds) {
            const msg = `Die Göttin hat dir eine neue Aufgabe zugewiesen: ${template.title}`;
            await createAppNotification(clientId, msg, '/tasks');
        }
    });
};

// --- MESSAGES ---

export const getConversations = async (role: UserRole, clientId?: string): Promise<Conversation[]> => {
    return retryOperation(async () => {
        let q = supabase.from('conversations').select('*, clients(name, is_paused)');
        if (role === UserRole.SUB && clientId) q = q.eq('client_id', clientId);
        const { data: convos = null, error = null } = await q.order('updated_at', { ascending: false });
        if (error) throw error;
        return await Promise.all((convos || []).map(async (c) => {
            const { count = null } = await supabase.from('messages').select('*', { count: 'exact', head: true }).eq('conversation_id', c.id).neq('sender', role).eq('is_read', false);
            const convo = mapConversationFromDB(c, count || 0);
            const { data: lastMsg = null } = await supabase.from('messages').select('*').eq('conversation_id', c.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
            if (lastMsg) { convo.lastMessage = mapMessageFromDB(lastMsg); convo.lastMessageTimestamp = lastMsg.created_at; }
            return convo;
        }));
    });
};

export const getMessages = async (conversationId: string): Promise<Message[]> => {
    if (conversationId.startsWith('temp-')) return [];
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('messages').select('*').eq('conversation_id', conversationId).order('created_at', { ascending: true });
        if (error) throw error;
        return (data || []).map(mapMessageFromDB);
    });
};

export const sendMessage = async (payload: SendMessagePayload): Promise<Message> => {
    return retryOperation(async () => {
        let convoId = payload.conversationId;
        if (convoId.startsWith('temp-')) {
            const clientId = convoId.replace('temp-', '');
            const newConvo = await getOrCreateConversation(clientId, payload.sender);
            if (!newConvo) throw new Error("Konnte Konversation nicht initialisieren.");
            convoId = newConvo.id;
        }
        const { data: msg = null, error = null } = await supabase.from('messages').insert({ 
            conversation_id: convoId, 
            sender: payload.sender, 
            content: payload.content, 
            type: payload.type, 
            reply_to: payload.replyTo, 
            audio_url: payload.audioUrl, 
            audio_duration: payload.audioDuration, 
            is_read: false 
        }).select().single();
        if (error) throw error;
        await supabase.from('conversations').update({ updated_at: new Date().toISOString() }).eq('id', convoId);
        
        // Notification Trigger
        try {
            const { data: convo } = await supabase.from('conversations').select('client_id, domina_id, clients(name)').eq('id', convoId).single();
            if (convo) {
                let recipientId = payload.sender === UserRole.DOMINA ? convo.client_id : convo.domina_id;
                
                // Fallback for Domina ID if not set in conversation
                if (!recipientId && payload.sender === UserRole.SUB) {
                    const { data: status } = await supabase.from('domina_status').select('domina_id').eq('id', 1).maybeSingle();
                    recipientId = status?.domina_id;
                }

                if (recipientId) {
                    const clientData = getJoinedData(convo, 'clients');
                    const senderName = payload.sender === UserRole.DOMINA ? 'Die Göttin' : (clientData?.name || 'Dein Klient');
                    const notificationMsg = `${senderName} hat dir eine Nachricht geschickt.`;
                    await createAppNotification(recipientId, notificationMsg, '/messenger');
                }
            }
        } catch (e) {
            console.warn("Failed to send message notification", e);
        }

        return mapMessageFromDB(msg);
    });
};

export const markConversationAsRead = async (conversationId: string, role: UserRole): Promise<void> => {
    if (conversationId.startsWith('temp-')) return;
    await retryOperation(async () => {
        const { error = null } = await supabase.from('messages').update({ is_read: true }).eq('conversation_id', conversationId).neq('sender', role).eq('is_read', false);
        if (error) throw error;
    });
};

export const getOrCreateConversation = async (clientId: string, role: UserRole): Promise<Conversation | null> => {
    return retryOperation(async () => {
        const { data: convos = null } = await supabase.from('conversations').select('*, clients(name, is_paused)').eq('client_id', clientId).order('updated_at', { ascending: false });
        if (convos?.length) {
            const primary = convos[0];
            const { count = null } = await supabase.from('messages').select('*', { count: 'exact', head: true }).eq('conversation_id', primary.id).neq('sender', role).eq('is_read', false);
            const convo = mapConversationFromDB(primary, count || 0);
            const { data: lastMsg = null } = await supabase.from('messages').select('*').eq('conversation_id', primary.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
            if (lastMsg) { convo.lastMessage = mapMessageFromDB(lastMsg); convo.lastMessageTimestamp = lastMsg.created_at; }
            return convo;
        }
        const { data: newConvo = null } = await supabase.from('conversations').insert({ client_id: clientId }).select('*, clients(name, is_paused)').single();
        if (newConvo) {
            // Try to set domina_id immediately if we have it
            try {
                const { data: status } = await supabase.from('domina_status').select('domina_id').eq('id', 1).maybeSingle();
                if (status?.domina_id) {
                    await supabase.from('conversations').update({ domina_id: status.domina_id }).eq('id', newConvo.id);
                    newConvo.domina_id = status.domina_id;
                }
            } catch (e) { /* ignore */ }
            return mapConversationFromDB(newConvo, 0);
        }
        return null;
    });
};

export const sendBroadcastMessage = async (clientIds: string[], content: string, attachment: File | null): Promise<void> => {
    for (const clientId of clientIds) {
        try {
            const convo = await getOrCreateConversation(clientId, UserRole.DOMINA);
            if (!convo) continue;

            let payload: SendMessagePayload = {
                conversationId: convo.id,
                sender: UserRole.DOMINA,
                content: attachment ? JSON.stringify({ text: content, file: { name: attachment.name, size: attachment.size, url: 'uploading...' } }) : content,
                type: attachment ? (attachment.type.startsWith('image/') ? 'IMAGE' : attachment.type.startsWith('video/') ? 'VIDEO' : 'FILE') : 'TEXT'
            };

            if (attachment) {
                payload = await addAttachmentMessage(payload, attachment, clientId);
            }
            
            await sendMessage(payload);
        } catch (err) {
            console.error(`Broadcast failed for client ${clientId}:`, err);
        }
    }
};

// --- TASKS ---

export const getFilteredTaskInstances = async (filters: any): Promise<TaskInstance[]> => {
    return retryOperation(async () => {
        // Rituals are strictly generated on the CURRENT day (today at/after midnight), never in advance!
        const todayStr = toLocalDateStr(new Date());

        // Clean up any accidentally pre-generated future pending rituals
        await cleanupFuturePreGeneratedRituals(filters.clientId && filters.clientId !== 'all' ? filters.clientId : undefined).catch(() => {});

        if (filters.clientId && filters.clientId !== 'all') {
            await autoGenerateDailyRituals(filters.clientId, todayStr).catch(e => console.error(`Ritual gen failed for ${todayStr}`, e));
        } else if (filters.isDominaView && filters.clientId === 'all') {
            // In the calendar "All Clients" view, ensure we have daily rituals for all active clients for today
            const { data: activeClients } = await supabase.from('clients').select('id').eq('is_active', true).eq('is_paused', false);
            if (activeClients && activeClients.length > 0) {
                const chunks = [];
                for (let i = 0; i < activeClients.length; i += 5) {
                    chunks.push(activeClients.slice(i, i + 5));
                }
                for (const chunk of chunks) {
                    await Promise.all(chunk.map(c => autoGenerateDailyRituals(c.id, todayStr).catch(() => {})));
                }
            }
        }

        let q = supabase.from('task_instances').select('*, clients(name, is_active, is_paused), task_labels(*)').order('end_date_time', { ascending: true }).order('id', { ascending: true });
        
        if (!filters.isDominaView && filters.currentUserId) q = q.eq('client_id', filters.currentUserId);
        if (filters.clientId && filters.clientId !== 'all') q = q.eq('client_id', filters.clientId);
        if (filters.templateId) q = q.eq('template_id', filters.templateId); 
        
        // Date Filtering Optimization
        if (!filters.ignoreDateFilter) {
            const now = new Date();
            const startLimit = new Date(now);
            startLimit.setDate(startLimit.getDate() - 30);
            const endLimit = new Date(now);
            endLimit.setDate(endLimit.getDate() + 60);
            
            q = q.gte('end_date_time', startLimit.toISOString()).lte('end_date_time', endLimit.toISOString());
        } else {
            // Even if ignoreDateFilter is true, we should probably have a safety limit or range
            // for the general view, to avoid fetching thousands of historical tasks
            const now = new Date();
            const startLimit = new Date(now);
            startLimit.setFullYear(startLimit.getFullYear() - 1); // 1 year back
            const endLimit = new Date(now);
            endLimit.setFullYear(endLimit.getFullYear() + 1); // 1 year forward
            
            q = q.gte('end_date_time', startLimit.toISOString()).lte('end_date_time', endLimit.toISOString());
        }

        const data = await fetchAllPages(q);
        return (data || []).map(mapTaskFromDB);
    });
};

const fetchAllPages = async (queryBuilder: any) => {
    const PAGE_SIZE = 250;
    let allData: any[] = [];
    let page = 0;
    let hasMore = true;
    while (hasMore) {
        const result = await retryOperation(async () => {
            const { data = null, error = null } = await queryBuilder.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
            if (error) throw error;
            return data;
        });
        if (result && result.length > 0) {
            allData = allData.concat(result);
            if (result.length < PAGE_SIZE) hasMore = false; else page++;
        } else hasMore = false;
    }
    // Deduplicate by ID to prevent React key warnings if paging overlaps
    if (allData.length > 0 && allData[0].id) {
        const unique = new Map();
        allData.forEach(item => unique.set(item.id, item));
        return Array.from(unique.values());
    }
    return allData;
};

export const updateTaskInstance = async (id: string, updates: Partial<TaskInstance>): Promise<TaskInstance> => {
    return retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.completedAt !== undefined) dbUpdates.completed_at = updates.completedAt;
        if (updates.durationProgress !== undefined) dbUpdates.duration_progress = updates.durationProgress;
        if (updates.countProgress !== undefined) dbUpdates.count_progress = updates.countProgress;
        if (updates.comments !== undefined) dbUpdates.comments = updates.comments;
        if (updates.proofs !== undefined) dbUpdates.proofs = updates.proofs;
        if (updates.attachmentLink !== undefined) dbUpdates.attachment_link = updates.attachmentLink;
        if (updates.goddessNotes !== undefined) dbUpdates.goddess_notes = updates.goddessNotes;
        if (updates.goddessReminders !== undefined) dbUpdates.goddess_reminders = updates.goddessReminders;
        if (updates.reviewRequired !== undefined) dbUpdates.review_required = updates.reviewRequired;
        if (updates.isReportRequired !== undefined) dbUpdates.is_report_required = updates.isReportRequired;
        if (updates.isProofRequired !== undefined) dbUpdates.is_proof_required = updates.isProofRequired;
        if (updates.labelId !== undefined) dbUpdates.label_id = updates.labelId;
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.startDateTime !== undefined) dbUpdates.start_date_time = updates.startDateTime;
        if (updates.endDateTime !== undefined) dbUpdates.end_date_time = updates.endDateTime;
        if (updates.visibilityDate !== undefined) dbUpdates.visibility_date = updates.visibilityDate;
        if (updates.durationTarget !== undefined) dbUpdates.duration_target = updates.durationTarget;
        if (updates.countTarget !== undefined) dbUpdates.count_target = updates.countTarget;
        if (updates.category !== undefined) dbUpdates.category = updates.category;
        if (updates.type !== undefined) dbUpdates.type = updates.type;
        if (updates.isBlocking !== undefined) dbUpdates.is_blocking = updates.isBlocking;
        if (updates.isMultiDay !== undefined) dbUpdates.is_multi_day = updates.isMultiDay;

        if (updates.coachingTopicId !== undefined || updates.coachingGoalId !== undefined || updates.coachingReflectionPrompt !== undefined || updates.coachingReflectionAnswer !== undefined || updates.coachingCatFeedback !== undefined) {
            const coachingMeta = {
                coachingTopicId: updates.coachingTopicId,
                coachingTopicTitle: updates.coachingTopicTitle,
                coachingGoalId: updates.coachingGoalId,
                coachingGoalTitle: updates.coachingGoalTitle,
                coachingReflectionPrompt: updates.coachingReflectionPrompt,
                coachingReflectionAnswer: updates.coachingReflectionAnswer,
                coachingCatFeedback: updates.coachingCatFeedback,
                originalNotes: updates.goddessNotes !== undefined ? updates.goddessNotes : ''
            };
            dbUpdates.goddess_notes = '__COACHING_META__' + JSON.stringify(coachingMeta);
        }

        const { data = null, error = null } = await supabase.from('task_instances').update(dbUpdates).eq('id', id).select('*, clients(name), task_labels(*)').single();
        if (error) throw error;
        
        // Notification Trigger
        try {
            const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', data.client_id).maybeSingle();
            let dominaId = convo?.domina_id;
            if (!dominaId) {
                dominaId = await getDominaId();
            }

            if (updates.status === TaskStatus.PENDING_REVIEW) {
                if (dominaId) {
                    const msg = `${data.clients?.name || 'Dein Klient'} hat eine Aufgabe zur Kontrolle eingereicht: ${data.title}`;
                    await createAppNotification(dominaId, msg, `/clients/${data.client_id}?tab=tasks`);
                }
            } else if (updates.status === TaskStatus.COMPLETED) {
                // If it was pending review, it was likely the Domina completing it
                // We don't easily know the previous status here without fetching it first, 
                // but we can assume if it's COMPLETED now, we notify the client.
                const msg = `Deine Aufgabe "${data.title}" wurde als erledigt markiert. Brav.`;
                await createAppNotification(data.client_id, msg, '/tasks');
                
                // Also notify Domina if it was a direct completion by client (no review)
                // For simplicity, we'll just notify Domina of all completions too
                if (dominaId) {
                    const msgD = `${data.clients?.name || 'Dein Klient'} hat die Aufgabe "${data.title}" abgeschlossen.`;
                    await createAppNotification(dominaId, msgD, `/clients/${data.client_id}?tab=tasks`);
                }
            } else if (updates.status === TaskStatus.FAILED || updates.status === TaskStatus.REJECTED) {
                const statusText = updates.status === TaskStatus.FAILED ? 'als gescheitert markiert' : 'abgelehnt';
                const msg = `Deine Aufgabe "${data.title}" wurde von der Göttin ${statusText}.`;
                await createAppNotification(data.client_id, msg, '/tasks');
            }
        } catch (e) {
            console.warn("Failed to send task status update notification", e);
        }

        return mapTaskFromDB(data);
    });
};

export const updateTaskInstances = async (taskId: string, updates: Partial<TaskInstance>, scope: 'single' | 'future' | 'all'): Promise<void> => {
    if (scope === 'single') { await updateTaskInstance(taskId, updates); return; }
    await retryOperation(async () => {
        const { data: rawTask = null } = await supabase.from('task_instances').select('*').eq('id', taskId).single();
        if (!rawTask) return;
        
        const task = mapTaskFromDB(rawTask);

        const dbUpdates: any = {};
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.startDateTime !== undefined) dbUpdates.start_date_time = updates.startDateTime;
        if (updates.endDateTime !== undefined) dbUpdates.end_date_time = updates.endDateTime;
        if (updates.visibilityDate !== undefined) dbUpdates.visibility_date = updates.visibilityDate;
        if (updates.durationTarget !== undefined) dbUpdates.duration_target = updates.durationTarget;
        if (updates.countTarget !== undefined) dbUpdates.count_target = updates.countTarget;
        if (updates.category !== undefined) dbUpdates.category = updates.category;
        if (updates.type !== undefined) dbUpdates.type = updates.type;
        if (updates.isBlocking !== undefined) dbUpdates.is_blocking = updates.isBlocking;
        if (updates.isMultiDay !== undefined) dbUpdates.is_multi_day = updates.isMultiDay;
        if (updates.attachmentLink !== undefined) dbUpdates.attachment_link = updates.attachmentLink;
        if (updates.goddessNotes !== undefined) dbUpdates.goddess_notes = updates.goddessNotes;
        if (updates.goddessReminders !== undefined) dbUpdates.goddess_reminders = updates.goddessReminders;
        if (updates.reviewRequired !== undefined) dbUpdates.review_required = updates.reviewRequired;
        if (updates.isReportRequired !== undefined) dbUpdates.is_report_required = updates.isReportRequired;
        if (updates.isProofRequired !== undefined) dbUpdates.is_proof_required = updates.isProofRequired;
        if (updates.labelId !== undefined) dbUpdates.label_id = updates.labelId;

        let q = supabase.from('task_instances').update(dbUpdates).eq('client_id', task.clientId);
        
        if (task.templateId) q = q.eq('template_id', task.templateId); else if (task.seriesId) q = q.eq('series_id', task.seriesId);
        
        if (scope === 'future') q = q.gte('end_date_time', task.endDateTime);
        const { error = null } = await q;
        if (error) throw error;
    });
};

export const deleteTaskInstances = async (task: TaskInstance, scope: 'single' | 'future' | 'all'): Promise<void> => {
    return retryOperation(async () => {
        // If deleting all/future instances of a template,
        // we unassign the client from the template so it doesn't immediately regenerate.
        if (((scope === 'all' || scope === 'future') && task.templateId)) {
            const { data: template } = await supabase.from('task_templates').select('assigned_client_ids').eq('id', task.templateId).single();
            if (template && template.assigned_client_ids && template.assigned_client_ids.includes(task.clientId)) {
                const newAssigned = template.assigned_client_ids.filter((id: string) => id !== task.clientId);
                await supabase.from('task_templates').update({ assigned_client_ids: newAssigned }).eq('id', task.templateId);
            }
        }

        if (scope === 'single' || (!task.seriesId && !task.templateId)) {  
            // If it's a recurring/ritual task, we don't physically delete it on 'single' delete
            // to prevent autoGenerateDailyRituals from recreating it immediately.
            if (scope === 'single' && (task.templateId || task.seriesId)) {
                const { error = null } = await supabase.from('task_instances').update({ 
                    status: TaskStatus.DELETED,
                    updated_at: new Date().toISOString()
                }).eq('id', task.id);
                if (error) throw error;
                return;
            }

            const { error = null } = await supabase.from('task_instances').delete().eq('id', task.id);
            if (error) throw error;
            return; 
        }
        
        let q = supabase.from('task_instances').delete().eq('client_id', task.clientId);
        if (task.templateId) q = q.eq('template_id', task.templateId); 
        else if (task.seriesId) q = q.eq('series_id', task.seriesId);
        
        if (scope === 'future') q = q.gte('end_date_time', task.endDateTime);
        
        const { error = null } = await q;
        if (error) throw error;
    });
};

export const deleteTaskInstance = async (id: string): Promise<void> => {
    return retryOperation(async () => {
        const { error = null } = await supabase.from('task_instances').delete().eq('id', id);
        if (error) throw error;
    });
};

export const addTaskInstanceDirectly = async (task: Omit<TaskInstance, 'id' | 'status' | 'comments'>): Promise<TaskInstance> => {
    return retryOperation(async () => {
        const validStart = task.startDateTime;
        let validEnd = task.endDateTime;
        if (new Date(validEnd).getTime() < new Date(validStart).getTime()) {
            validEnd = validStart;
        }

        let finalNotes = task.goddessNotes || '';
        const hasCoaching = !!(task.coachingTopicId || task.coachingGoalId || task.coachingReflectionPrompt || task.coachingTopicTitle || task.coachingGoalTitle);
        if (hasCoaching) {
            const meta = {
                coachingTopicId: task.coachingTopicId,
                coachingTopicTitle: task.coachingTopicTitle,
                coachingGoalId: task.coachingGoalId,
                coachingGoalTitle: task.coachingGoalTitle,
                coachingReflectionPrompt: task.coachingReflectionPrompt,
                coachingReflectionAnswer: task.coachingReflectionAnswer,
                coachingCatFeedback: task.coachingCatFeedback,
                originalNotes: task.goddessNotes || ''
            };
            finalNotes = '__COACHING_META__' + JSON.stringify(meta);
        }

        const { data = null, error = null } = await supabase.from('task_instances').insert({
            client_id: task.clientId,
            title: task.title,
            description: task.description,
            category: task.category,
            type: task.type,
            status: TaskStatus.PENDING,
            start_date_time: validStart,
            end_date_time: validEnd,
            visibility_date: task.visibilityDate,
            label_id: task.labelId,
            review_required: task.reviewRequired,
            is_report_required: task.isReportRequired,
            is_proof_required: task.isProofRequired,
            duration_target: task.durationTarget,
            count_target: task.countTarget,
            attachment_link: task.attachmentLink,
            goddess_notes: finalNotes,
            goddess_reminders: task.goddessReminders,
            is_blocking: task.isBlocking,
            is_multi_day: !!task.isMultiDay
        }).select('*, clients(name), task_labels(*)').single();
        if (error) throw error;

        // Notification Trigger
        const msg = `Die Göttin hat dir eine neue Aufgabe zugewiesen: ${task.title}`;
        await createAppNotification(task.clientId, msg, '/tasks');

        return mapTaskFromDB(data);
    });
};

export const createTaskInstance = addTaskInstanceDirectly;

export const addPersonalRecurringEvents = async (
    baseEvent: Omit<TaskInstance, 'id' | 'status' | 'comments' | 'label'>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _recurrence?: RecurrenceType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _recurrenceEndType?: 'DATE' | 'INDEFINITE',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _recurrenceEndDate?: string
): Promise<void> => {
    // For now, we just add the single base event as a placeholder for the recurring logic
        await addTaskInstanceDirectly(baseEvent as any);
};

// --- TO-DOS (KLIENTEN TO-DO LISTE: TAG, WOCHE, MONAT) ---

export const createClientTodo = async (
    clientId: string, 
    todo: { 
        title: string; 
        description?: string; 
        dueDateTime: string; 
        scope: TodoScope;
    }
): Promise<TaskInstance> => {
    return retryOperation(async () => {
        const now = new Date().toISOString();
        const meta = {
            isTodo: true,
            scope: todo.scope,
            createdAt: now
        };
        const goddessNotes = `__TODO_META__${JSON.stringify(meta)}`;

        const endDateTime = todo.dueDateTime;
        const startDate = new Date(endDateTime);
        startDate.setMinutes(startDate.getMinutes() - 30);
        const startDateTime = startDate.toISOString();

        const task = await addTaskInstanceDirectly({
            clientId,
            templateId: null,
            createdAt: now,
            updatedAt: now,
            title: todo.title,
            description: todo.description ? `__TODO__ ${todo.description}` : '__TODO__',
            category: TaskCategory.PERSONAL,
            type: TaskType.SIMPLE,
            startDateTime,
            endDateTime,
            visibilityDate: now,
            reviewRequired: false,
            isReportRequired: false,
            isProofRequired: false,
            labelId: null,
            attachmentLink: undefined,
            goddessNotes,
            isBlocking: false,
            isMultiDay: false
        });

        return {
            ...task,
            isTodo: true,
            todoScope: todo.scope
        };
    });
};

export const getClientTodos = async (clientId: string): Promise<TaskInstance[]> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase
            .from('task_instances')
            .select('*, clients(name), task_labels(*)')
            .eq('client_id', clientId)
            .or('description.ilike.%__TODO__%,goddess_notes.ilike.%__TODO_META__%,category.eq."Eigener Termin"')
            .order('end_date_time', { ascending: true });

        if (error) throw error;
        const mapped = (data || []).map(mapTaskFromDB);
        return mapped.filter(t => t.isTodo || t.description?.includes('__TODO__') || t.goddessNotes?.includes('__TODO_META__'));
    });
};

export const toggleClientTodo = async (todoId: string, isCompleted: boolean): Promise<TaskInstance> => {
    return retryOperation(async () => {
        return updateTaskInstance(todoId, {
            status: isCompleted ? TaskStatus.COMPLETED : TaskStatus.PENDING,
            completedAt: isCompleted ? new Date().toISOString() : undefined
        });
    });
};

export const deleteClientTodo = async (todoId: string): Promise<void> => {
    return retryOperation(async () => {
        await deleteTaskInstance(todoId);
    });
};

// --- REPORTS ---

export const getReportSubmissions = async (filters: any): Promise<ReportSubmission[]> => { 
    return retryOperation(async () => {
        let q = supabase.from('report_submissions').select('*, clients(name), report_templates(title)').order('submitted_at', { ascending: false }); 
        if (filters.templateId) q = q.eq('template_id', filters.templateId); 
        if (filters.clientId) q = q.eq('client_id', filters.clientId); 
        const { data = null, error = null } = await q; 
        if (error) throw error; 
        return (data || []).map(s => {
            const clientData = getJoinedData(s, 'clients');
            const templateData = getJoinedData(s, 'report_templates');

            let feedback: ReportSubmissionFeedback | undefined = s.feedback || s.goddess_feedback;
            if (!feedback && Array.isArray(s.answers)) {
                const fbAnswer = s.answers.find((a: any) => a.questionId === '__GODDESS_FEEDBACK__');
                if (fbAnswer && fbAnswer.value) {
                    try {
                        feedback = typeof fbAnswer.value === 'string' ? JSON.parse(fbAnswer.value) : fbAnswer.value;
                    } catch {
                        feedback = { notes: String(fbAnswer.value) };
                    }
                }
            }

            return {
                ...s,
                templateId: s.template_id,
                clientId: s.client_id,
                submittedAt: s.submitted_at,
                clientName: clientData?.name,
                templateTitle: templateData?.title,
                feedback
            };
        });
    });
};

export const addReportFeedback = async (
    submissionId: string, 
    feedback: ReportSubmissionFeedback
): Promise<ReportSubmissionFeedback> => {
    return retryOperation(async () => {
        const feedbackObj = {
            ...feedback,
            createdAt: feedback.createdAt || new Date().toISOString()
        };

        let updated = false;
        try {
            const { error } = await supabase
                .from('report_submissions')
                .update({ feedback: feedbackObj })
                .eq('id', submissionId);
            if (!error) updated = true;
        } catch {
            // column feedback might not exist
        }

        if (!updated) {
            const { data: subData } = await supabase
                .from('report_submissions')
                .select('answers')
                .eq('id', submissionId)
                .single();
            if (subData) {
                const currentAnswers: ReportAnswer[] = Array.isArray(subData.answers) ? subData.answers : [];
                const filtered = currentAnswers.filter((a: any) => a.questionId !== '__GODDESS_FEEDBACK__');
                filtered.push({
                    questionId: '__GODDESS_FEEDBACK__',
                    value: JSON.stringify(feedbackObj)
                });
                const { error: err2 } = await supabase
                    .from('report_submissions')
                    .update({ answers: filtered })
                    .eq('id', submissionId);
                if (err2) throw err2;
            }
        }

        // Notify client
        try {
            const { data: subInfo } = await supabase
                .from('report_submissions')
                .select('client_id, template_id, report_templates(title)')
                .eq('id', submissionId)
                .maybeSingle();

            if (subInfo?.client_id) {
                const tmplTitle = (subInfo as any).report_templates?.title || 'Check-In';
                
                await createAppNotification(
                    subInfo.client_id,
                    `✨ Cat hat dir Feedback zu deinem Check-In '${tmplTitle}' gegeben.`,
                    '/check-in'
                );

                const { data: convo } = await supabase
                    .from('conversations')
                    .select('id')
                    .eq('client_id', subInfo.client_id)
                    .maybeSingle();
                if (convo?.id) {
                    await sendMessage({
                        conversationId: convo.id,
                        sender: UserRole.DOMINA,
                        content: `✨ FEEDBACK ZU DEINEM CHECK-IN (»${tmplTitle}«)\n\n"${feedback.notes}"${feedback.rating ? `\n\nBewertung: ${feedback.rating}/10` : ''}\n\n👉 Öffne deine Check-In Rubrik für alle Details.`,
                        type: 'TEXT'
                    });
                }
            }
        } catch (notifErr) {
            console.warn("Feedback notification error:", notifErr);
        }

        return feedbackObj;
    });
};

export const FAIRYREALM_WEEKLY_CHECKIN_ID = 'fairyrealm-weekly-checkin';

export const FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE: ReportTemplate = {
    id: FAIRYREALM_WEEKLY_CHECKIN_ID,
    title: 'FAIRYREALM · WÖCHENTLICHER CHECK-IN',
    description: 'Zeitraum: Diese Woche',
    sections: [
        {
            id: 'sec_1_personal',
            title: '1. Persönlicher Rückblick',
            description: 'Reflexion deines emotionalen Zustands, deiner Energie und der prägenden Momente dieser Woche.',
            questions: [
                {
                    id: 'q_1_emo',
                    text: 'Emotionale Verfassung',
                    type: ReportQuestionType.LINEAR_SCALE,
                    isRequired: true,
                    scaleMin: 1,
                    scaleMax: 10,
                    scaleMinLabel: '1 (Sehr niedrig / belastet)',
                    scaleMaxLabel: '10 (Hervorragend / stabil)'
                },
                {
                    id: 'q_1_energie',
                    text: 'Energie',
                    type: ReportQuestionType.LINEAR_SCALE,
                    isRequired: true,
                    scaleMin: 1,
                    scaleMax: 10,
                    scaleMinLabel: '1 (Erschöpft / kraftlos)',
                    scaleMaxLabel: '10 (Voller Energie / kraftvoll)'
                },
                {
                    id: 'q_1_review',
                    text: 'Wochenrückblick',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_1_stolz',
                    text: 'Stolz',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_1_challenge',
                    text: 'Herausforderung',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        },
        {
            id: 'sec_2_goals',
            title: '2. Ziele',
            description: 'Status deiner Zielerreichung, konkrete Umsetzungsschritte und gelebte Eigenverantwortung.',
            questions: [
                {
                    id: 'q_2_status',
                    text: 'Zielstatus',
                    type: ReportQuestionType.MULTIPLE_CHOICE,
                    isRequired: true,
                    options: [
                        { id: 'opt_teil', value: 'Teilweise erreicht' },
                        { id: 'opt_voll', value: 'Vollständig erreicht' },
                        { id: 'opt_nicht', value: 'Nicht erreicht' },
                        { id: 'opt_ueber', value: 'Übertroffen' }
                    ]
                },
                {
                    id: 'q_2_steps',
                    text: 'Konkrete Schritte',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_2_obstacles',
                    text: 'Hindernisse und Eigenverantwortung',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        },
        {
            id: 'sec_3_relations',
            title: '3. Beziehungen',
            description: 'Reflexion über Partnerschaft, Familie, Freunde und dein soziales Umfeld in dieser Woche.',
            questions: [
                {
                    id: 'q_3_relations_text',
                    text: 'Beziehungen',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        },
        {
            id: 'sec_4_career',
            title: '4. Beruf',
            description: 'Berufliche Schwerpunkte, geschäftlicher Fortschritt und berufliche Herausforderungen.',
            questions: [
                {
                    id: 'q_4_career_text',
                    text: 'Beruf',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        },
        {
            id: 'sec_5_discipline',
            title: '5. Disziplin und Selbstführung',
            description: 'Konsequenz in deinen Gewohnheiten, Tagesstrukturen und deiner inneren Haltung.',
            questions: [
                {
                    id: 'q_5_konsequenz',
                    text: 'Konsequenz',
                    type: ReportQuestionType.LINEAR_SCALE,
                    isRequired: true,
                    scaleMin: 1,
                    scaleMax: 10,
                    scaleMinLabel: '1 (Sehr inkonsequent)',
                    scaleMaxLabel: '10 (Kompromisslose Disziplin)'
                }
            ]
        },
        {
            id: 'sec_6_connection',
            title: '6. Verbindung zu Cat',
            description: 'Deine Ausrichtung auf die Göttin, Hingabe, Führung und offene Bedürfnisse.',
            questions: [
                {
                    id: 'q_6_connection_scale',
                    text: 'Verbindung',
                    type: ReportQuestionType.LINEAR_SCALE,
                    isRequired: true,
                    scaleMin: 1,
                    scaleMax: 10,
                    scaleMinLabel: '1 (Entfernt / Abgelenkt)',
                    scaleMaxLabel: '10 (Tief verbunden / Präsent)'
                },
                {
                    id: 'q_6_leadership',
                    text: 'Erlebte Führung',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_6_wishes',
                    text: 'Wünsche und Unterstützung',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        },
        {
            id: 'sec_7_next_week',
            title: '7. Kommende Woche',
            description: 'Fokus, klare Prioritäten und verbindliche Vereinbarungen für die nächste Woche.',
            questions: [
                {
                    id: 'q_7_priorities',
                    text: 'Prioritäten',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_7_commitment',
                    text: 'Verbindliche Zusage',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                },
                {
                    id: 'q_7_support_needed',
                    text: 'Benötigte Unterstützung',
                    type: ReportQuestionType.PARAGRAPH,
                    isRequired: false
                }
            ]
        }
    ]
};

export const getReportTemplates = async (): Promise<ReportTemplate[]> => { 
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('report_templates').select('*').order('created_at');
        if (error) throw error;
        
        const list = data || [];
        const hasWeeklyCheckIn = list.some(t => t.title.trim().toLowerCase() === FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title.trim().toLowerCase());
        
        if (!hasWeeklyCheckIn) {
            // Attempt auto-insert to Supabase, but always prepend template in client response
            try {
                const { data: inserted } = await supabase.from('report_templates').insert({
                    title: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title,
                    description: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.description,
                    sections: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.sections
                }).select().single();
                if (inserted) {
                    return [inserted, ...list];
                }
            } catch {
                // If RLS prevents insertion, return with fallback master ID
            }
            return [FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE, ...list];
        }
        
        // Put the official weekly check-in first in the list
        return list.sort((a, b) => {
            if (a.title.includes('FAIRYREALM')) return -1;
            if (b.title.includes('FAIRYREALM')) return 1;
            return 0;
        });
    });
};

export const getReportTemplateById = async (id: string): Promise<ReportTemplate | null> => { 
    if (id === FAIRYREALM_WEEKLY_CHECKIN_ID) {
        return FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE;
    }
    return retryOperation(async () => {
        const { data = null } = await supabase.from('report_templates').select('*').eq('id', id).maybeSingle();
        if (!data && id.includes('fairyrealm')) {
            return FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE;
        }
        return data;
    });
};

export const addReportTemplate = async (t: Omit<ReportTemplate, 'id'>): Promise<ReportTemplate> => { return retryOperation(async () => { const { data, error = null } = await supabase.from('report_templates').insert(t).select().single(); if (error) throw error; return data; }); };

export const updateReportTemplate = async (id: string, updates: Partial<ReportTemplate>): Promise<void> => { 
    await retryOperation(async () => {
        const { error = null } = await supabase.from('report_templates').update({ title: updates.title, description: updates.description, sections: updates.sections }).eq('id', id);
        if (error) throw error;
    }); 
};

export const deleteReportTemplate = async (id: string): Promise<void> => { 
    await retryOperation(async () => {
        await supabase.from('report_templates').delete().eq('id', id);
        const { data: allClients } = await supabase.from('clients').select('id, profile, report_template_id');
        if (allClients) {
            for (const cl of allClients) {
                const profile = cl.profile || {};
                const currentTids: string[] = Array.isArray(profile.reportTemplateIds)
                    ? profile.reportTemplateIds
                    : (cl.report_template_id ? [cl.report_template_id] : []);
                if (currentTids.includes(id) || cl.report_template_id === id) {
                    const updatedTids = currentTids.filter((tid: string) => tid !== id);
                    await supabase.from('clients').update({
                        report_template_id: updatedTids[0] || null,
                        profile: { ...profile, reportTemplateIds: updatedTids }
                    }).eq('id', cl.id);
                }
            }
        }
    });
};

export const assignReportTemplateToClients = async (tid: string, cids: string[]): Promise<void> => { 
    await retryOperation(async () => { 
        let resolvedTid = tid;
        // If assigning static master template, ensure it is created in DB first
        if (tid === FAIRYREALM_WEEKLY_CHECKIN_ID) {
            const { data: existing } = await supabase.from('report_templates').select('id').eq('title', FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title).maybeSingle();
            if (existing?.id) {
                resolvedTid = existing.id;
            } else {
                try {
                    const { data: inserted } = await supabase.from('report_templates').insert({
                        title: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title,
                        description: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.description,
                        sections: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.sections
                    }).select('id').single();
                    if (inserted?.id) resolvedTid = inserted.id;
                } catch {
                    // fallback
                }
            }
        }

        const { data: allClients } = await supabase.from('clients').select('id, profile, report_template_id');
        if (allClients) {
            for (const cl of allClients) {
                const isSelected = cids.includes(cl.id);
                const profile = cl.profile || {};
                const existingTids: string[] = Array.isArray(profile.reportTemplateIds)
                    ? profile.reportTemplateIds
                    : (cl.report_template_id ? [cl.report_template_id] : []);
                
                const hasTemplate = existingTids.includes(resolvedTid) || (tid === FAIRYREALM_WEEKLY_CHECKIN_ID && existingTids.includes(FAIRYREALM_WEEKLY_CHECKIN_ID));

                if (isSelected && !hasTemplate) {
                    const updatedTids = Array.from(new Set([...existingTids, resolvedTid]));
                    await supabase.from('clients').update({
                        report_template_id: updatedTids[0] || null,
                        profile: { ...profile, reportTemplateIds: updatedTids }
                    }).eq('id', cl.id);
                } else if (!isSelected && hasTemplate) {
                    const updatedTids = existingTids.filter((id: string) => id !== resolvedTid && id !== tid);
                    await supabase.from('clients').update({
                        report_template_id: updatedTids[0] || null,
                        profile: { ...profile, reportTemplateIds: updatedTids }
                    }).eq('id', cl.id);
                }
            }
        }
    }); 
};

export const sendCheckInToClient = async (params: {
    templateId: string;
    clientIds: string[];
    deadlineDate?: string;
    deadlineTime?: string;
    personalMessage?: string;
    notifyInChat?: boolean;
    createCalendarTask?: boolean;
}): Promise<void> => {
    const {
        templateId,
        clientIds,
        deadlineDate,
        deadlineTime,
        personalMessage,
        notifyInChat = true,
        createCalendarTask = true
    } = params;

    if (!clientIds || clientIds.length === 0) return;

    // 1. Resolve template ID & title
    let actualTemplateId = templateId;
    let templateTitle = 'FAIRYREALM · WÖCHENTLICHER CHECK-IN';

    if (templateId === FAIRYREALM_WEEKLY_CHECKIN_ID) {
        const { data: existing } = await supabase.from('report_templates').select('id, title').eq('title', FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title).maybeSingle();
        if (existing?.id) {
            actualTemplateId = existing.id;
        } else {
            try {
                const { data: inserted } = await supabase.from('report_templates').insert({
                    title: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.title,
                    description: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.description,
                    sections: FAIRYREALM_WEEKLY_CHECKIN_TEMPLATE.sections
                }).select('id').single();
                if (inserted?.id) actualTemplateId = inserted.id;
            } catch (e) {
                console.warn('Could not insert master template, continuing with ID', e);
            }
        }
    } else {
        const { data: tmpl } = await supabase.from('report_templates').select('title').eq('id', templateId).maybeSingle();
        if (tmpl?.title) templateTitle = tmpl.title;
    }

    // 2. Assign to clients
    await assignReportTemplateToClients(actualTemplateId, clientIds);

    const now = new Date();
    const dDate = deadlineDate || new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const dTime = deadlineTime || '20:00';
    const dueDateTime = `${dDate}T${dTime}:00`;

    // 3. For each client, send chat notification, in-app notification & calendar task
    for (const clientId of clientIds) {
        if (notifyInChat) {
            try {
                const { data: convo } = await supabase.from('conversations').select('id').eq('client_id', clientId).maybeSingle();
                if (convo?.id) {
                    const chatText = `✨ WÖCHENTLICHER CHECK-IN ZUGEWIESEN\n\nCat hat dir deinen wöchentlichen Check-In zugewiesen:\n»${templateTitle}«\nFälligkeit: ${new Date(dueDateTime).toLocaleString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} Uhr${personalMessage ? `\n\nBegleitnotiz von Cat:\n"${personalMessage}"` : ''}\n\n👉 Öffne die Rubrik "Check-In" im Menü, um deine Reflexion einzureichen.`;
                    
                    await sendMessage({
                        conversationId: convo.id,
                        sender: UserRole.DOMINA,
                        content: chatText,
                        type: 'TEXT'
                    });
                }
            } catch (err) {
                console.error(`Failed to send chat notification to ${clientId}`, err);
            }
        }

        try {
            await supabase.from('app_notifications').insert({
                user_id: clientId,
                content: `✨ Wöchentlicher Check-In '${templateTitle}' wurde dir zugewiesen. Bitte zeitnah ausfüllen.`,
                link: '/check-in',
                is_read: false
            });
        } catch (err) {
            console.error(`Failed to send app notification to ${clientId}`, err);
        }

        if (createCalendarTask) {
            try {
                await addTaskInstanceDirectly({
                    clientId,
                    templateId: null,
                    labelId: null,
                    title: `Wöchentlicher Check-In: ${templateTitle}`,
                    description: personalMessage || 'Fülle deinen wöchentlichen Check-In in der Rubrik Check-In gewissenhaft aus.',
                    category: TaskCategory.SUB,
                    type: TaskType.SIMPLE,
                    startDateTime: now.toISOString(),
                    endDateTime: dueDateTime,
                    visibilityDate: now.toISOString(),
                    reviewRequired: true,
                    isReportRequired: true,
                    isProofRequired: false,
                    createdAt: now.toISOString(),
                    updatedAt: now.toISOString()
                });
            } catch (err) {
                console.error(`Failed to create calendar task for ${clientId}`, err);
            }
        }
    }
};

export const addReportSubmission = async (s: Omit<ReportSubmission, 'id'>): Promise<void> => { 
    await retryOperation(async () => { 
        const { error = null } = await supabase.from('report_submissions').insert({ 
            template_id: s.templateId, 
            client_id: s.clientId, 
            submitted_at: s.submittedAt, 
            answers: s.answers 
        }); 
        if (error) throw error; 

        // Notification Trigger
        try {
            const { data: client } = await supabase.from('clients').select('name').eq('id', s.clientId).single();
            const { data: template } = await supabase.from('report_templates').select('title').eq('id', s.templateId).single();
            
            const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', s.clientId).maybeSingle();
            let recipientId = convo?.domina_id;
            
            if (!recipientId) {
                recipientId = await getDominaId();
            }

            if (recipientId) {
                const msg = `${client?.name || 'Dein Klient'} hat einen Bericht eingereicht: ${template?.title || 'Unbekannt'}`;
                await createAppNotification(recipientId, msg, `/clients/${s.clientId}?tab=reports`);
            }
        } catch (e) {
            console.warn("Failed to send report notification", e);
        }
    }); 
};

export const uploadReportFile = async (f: File, cid: string, tid: string, qid: string): Promise<{ name: string, url: string }> => { return retryOperation(async () => { const fn = `reports/${cid}/${tid}/${qid}/${uuid()}.${f.name.split('.').pop()}`; await supabase.storage.from('media').upload(fn, f); const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fn); return { name: f.name, url: publicUrl }; }); };

// --- DIARY ---

export const getDiaryEntries = async (clientId: string, limit: number = 100): Promise<DiaryEntry[]> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('diary_entries')
            .select('*')
            .eq('client_id', clientId)
            .order('date', { ascending: false })
            .limit(limit);
        if (error) throw error;
        return (data || []).map(mapDiaryEntryFromDB);
    });
};

export const getPendingDayOffRequests = async (activeClientIds: string[]): Promise<DiaryEntry[]> => {
    if (activeClientIds.length === 0) return [];
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase
            .from('diary_entries')
            .select('*')
            .in('client_id', activeClientIds)
            .eq('type', 'DAY_OFF_REQUEST')
            .is('goddess_feedback', null);
        if (error) throw error;
        return (data || []).map(mapDiaryEntryFromDB);
    });
};

export const addDiaryEntry = async (entry: Omit<DiaryEntry, 'id'>): Promise<DiaryEntry> => {
    return retryOperation(async () => {
        const insertPayload: Record<string, any> = {
            client_id: entry.clientId,
            author: entry.author,
            date: entry.date,
            end_date: entry.endDate,
            type: entry.type,
            content: entry.content,
            goddess_feedback: entry.goddessFeedback
        };
        if (entry.coachingTopicId) insertPayload.coaching_topic_id = entry.coachingTopicId;
        if (entry.coachingTopicTitle) insertPayload.coaching_topic_title = entry.coachingTopicTitle;
        if (entry.coachingGoalId) insertPayload.coaching_goal_id = entry.coachingGoalId;
        if (entry.coachingGoalTitle) insertPayload.coaching_goal_title = entry.coachingGoalTitle;

        let { data = null, error = null } = await supabase.from('diary_entries').insert(insertPayload).select().single();
        if (error && (error.code === '42703' || error.message?.includes('column'))) {
            const fallbackPayload = {
                client_id: entry.clientId,
                author: entry.author,
                date: entry.date,
                end_date: entry.endDate,
                type: entry.type,
                content: entry.content,
                goddess_feedback: entry.goddessFeedback
            };
            const fallbackRes = await supabase.from('diary_entries').insert(fallbackPayload).select().single();
            data = fallbackRes.data;
            error = fallbackRes.error;
        }
        if (error) throw error;
        
        // Notification Trigger
        if (entry.author === 'SUB') {
            try {
                const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', entry.clientId).maybeSingle();
                let recipientId = convo?.domina_id;

                if (!recipientId) {
                    recipientId = await getDominaId();
                }

                if (recipientId) {
                    const { data: client } = await supabase.from('clients').select('name').eq('id', entry.clientId).single();
                    const msg = `${client?.name || 'Dein Klient'} hat einen neuen Tagebucheintrag verfasst.`;
                    await createAppNotification(recipientId, msg, `/clients/${entry.clientId}?tab=diary`);
                }
            } catch (e) {
                console.warn("Failed to send diary notification", e);
            }
        }

        return mapDiaryEntryFromDB(data);
    });
};

export const updateDiaryEntry = async (id: string, updates: Partial<DiaryEntry>): Promise<DiaryEntry> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('diary_entries').update({ content: updates.content, goddess_feedback: updates.goddessFeedback }).eq('id', id).select().single();
        if (error) throw error;
        
        // Notification Trigger
        if (updates.goddessFeedback) {
            try {
                const msg = `Die Göttin hat dein Tagebuch kommentiert.`;
                await createAppNotification(data.client_id, msg, '/diary');
            } catch (e) {
                console.warn("Failed to send diary feedback notification", e);
            }
        }

        return mapDiaryEntryFromDB(data);
    });
};

export const requestDayOff = async (clientId: string, startDate: string, endDate: string, reason: string): Promise<void> => {
    await addDiaryEntry({
        clientId,
        author: 'SUB',
        date: startDate,
        endDate: endDate,
        type: 'DAY_OFF_REQUEST',
        content: reason
    });
};

export const decideOnDayOffRequest = async (requestId: string, approve: boolean, notes: string): Promise<void> => {
    await retryOperation(async () => {
        const { data: entry } = await supabase.from('diary_entries').select('client_id').eq('id', requestId).single();
        
        const { error = null } = await supabase.from('diary_entries').update({
            goddess_feedback: {
                isApproved: approve,
                notes: notes,
                tags: []
            }
        }).eq('id', requestId);
        if (error) throw error;
        
        // Notification Trigger
        if (entry?.client_id) {
            const msg = `Deine Anfrage wurde ${approve ? 'genehmigt' : 'abgelehnt'}.`;
            await createAppNotification(entry.client_id, msg, '/diary');
        }
    });
};

// --- PAYMENTS ---

export const getPayments = async (clientId?: string, limit: number = 500): Promise<Payment[]> => {
    return retryOperation(async () => {
        let query = supabase.from('payments').select('*, clients(name)').order('date', { ascending: false }).limit(limit);
        if (clientId) query = query.eq('client_id', clientId);
        const { data = null, error = null } = await query;
        if (error) {
            // Fallback if relations query cannot be resolved
            let fallbackQuery = supabase.from('payments').select('*').order('date', { ascending: false }).limit(limit);
            if (clientId) fallbackQuery = fallbackQuery.eq('client_id', clientId);
            const { data: fbData, error: fbError } = await fallbackQuery;
            if (fbError) throw fbError;
            return (fbData || []).map(mapPaymentFromDB);
        }
        return (data || []).map(mapPaymentFromDB);
    });
};

export const addPayment = async (payment: Omit<Payment, 'id'>): Promise<Payment> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('payments').insert({
            client_id: payment.clientId,
            amount: payment.amount,
            currency: payment.currency,
            method: payment.method,
            date: payment.date,
            purpose: payment.purpose,
            description: payment.description,
            tribute_start_date: payment.tributeStartDate,
            tribute_end_date: payment.tributeEndDate
        }).select().single();
        if (error) throw error;

        // Notification Trigger
        try {
            const { data: client } = await supabase.from('clients').select('name').eq('id', payment.clientId).single();
            const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', payment.clientId).maybeSingle();
            let recipientId = convo?.domina_id;
            if (!recipientId) {
                recipientId = await getDominaId();
            }
            if (recipientId) {
                const msg = `Neue Zahlung von ${client?.name || 'Unbekannt'} registriert: ${payment.amount} ${payment.currency}`;
                await createAppNotification(recipientId, msg, `/clients/${payment.clientId}?tab=payments`);
            }
        } catch (e) {
            console.warn("Failed to send payment notification", e);
        }

        return mapPaymentFromDB(data);
    });
};

export const updatePayment = async (id: string, updates: Partial<Payment>): Promise<Payment> => {
    return retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.clientId !== undefined) dbUpdates.client_id = updates.clientId;
        if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
        if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
        if (updates.method !== undefined) dbUpdates.method = updates.method;
        if (updates.date !== undefined) dbUpdates.date = updates.date;
        if (updates.purpose !== undefined) dbUpdates.purpose = updates.purpose;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.tributeStartDate !== undefined) dbUpdates.tribute_start_date = updates.tributeStartDate;
        if (updates.tributeEndDate !== undefined) dbUpdates.tribute_end_date = updates.tributeEndDate;

        const { data = null, error = null } = await supabase.from('payments').update(dbUpdates).eq('id', id).select().single();
        if (error) throw error;

        // Notification Trigger (Notify Sub if status changed or comment added)
        try {
            const msg = `Deine Zahlung wurde aktualisiert.`;
            await createAppNotification(data.client_id, msg, '/payments');
        } catch (e) {
            console.warn("Failed to send payment update notification", e);
        }

        return mapPaymentFromDB(data);
    });
};

export const deletePayment = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('payments').delete().eq('id', id);
        if (error) throw error;
    });
};

// --- LABELS ---

export const getTaskLabels = async (): Promise<TaskLabel[]> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('task_labels').select('*').order('name');
        if (error) throw error;
        return (data || []).map(l => ({ id: l.id, userId: l.user_id, name: l.name, color: l.color }));
    });
};

export const addTaskLabel = async (label: Omit<TaskLabel, 'id' | 'userId'>): Promise<TaskLabel> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('task_labels').insert({
            name: label.name,
            color: label.color
        }).select().single();
        if (error) throw error;
        return { id: data.id, userId: data.user_id, name: data.name, color: data.color };
    });
};

export const updateTaskLabel = async (id: string, updates: Partial<TaskLabel>): Promise<void> => {
    await retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.color !== undefined) dbUpdates.color = updates.color;
        if (updates.userId !== undefined) dbUpdates.user_id = updates.userId;

        const { error = null } = await supabase.from('task_labels').update(dbUpdates).eq('id', id);
        if (error) throw error;
    });
};

export const deleteTaskLabel = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('task_labels').delete().eq('id', id);
        if (error) throw error;
    });
};

// --- OTHERS ---

export const createAppNotification = async (uidOrKeyword: string | null | undefined, message: string, link: string): Promise<void> => {
    if (!uidOrKeyword) {
        console.warn("createAppNotification: No recipient ID provided", { message, link });
        return;
    }
    try {
        let targetUid = uidOrKeyword;

        // Resolve 'domina' keyword to actual UUID
        if (uidOrKeyword === 'domina') {
            try {
                const { data } = await supabase.from('domina_status').select('domina_id').eq('id', 1).maybeSingle();
                if (data?.domina_id) {
                    targetUid = data.domina_id;
                    console.log(`[Notification] Resolved 'domina' to ${targetUid}`);
                } else {
                    const { data: authData } = await supabase.auth.getUser();
                    if (authData.user?.id) {
                        targetUid = authData.user.id;
                        console.log(`[Notification] Resolved 'domina' via auth user to ${targetUid}`);
                    } else {
                        console.warn("[Notification] Could not resolve 'domina' keyword - domina ID missing");
                        return;
                    }
                }
            } catch (e) {
                console.error("[Notification] Error resolving 'domina' ID:", e);
                const { data: authData } = await supabase.auth.getUser();
                if (authData.user?.id) {
                    targetUid = authData.user.id;
                } else {
                    return;
                }
            }
        }

        // 1. In-App Notification (Supabase)
        await retryOperation(async () => {
            const { error } = await supabase.from('app_notifications').insert({
                user_id: targetUid,
                content: message,
                link,
                is_read: false,
                created_at: new Date().toISOString()
            });
            if (error) throw error;
        }, 1);

        // 2. Push Notification (Telegram via Server API)
        try {
            await triggerPushNotification(targetUid, message, link);
        } catch {
            // Push notification is best-effort and should never fail in-app notifications
        }
    } catch (err) {
        console.warn("Silent notification failure:", { err, uidOrKeyword, message, link });
    }
};

export const triggerPushNotification = async (
    uid: string, 
    body: string, 
    linkOrSendAfter?: string, 
    sendAfterParam?: string
): Promise<string | null> => {
    try {
        let link = '/';
        let sendAfter = sendAfterParam;
        if (linkOrSendAfter) {
            if (linkOrSendAfter.startsWith('/') || linkOrSendAfter.startsWith('http')) {
                link = linkOrSendAfter;
            } else {
                sendAfter = linkOrSendAfter;
            }
        }

        // Build full absolute URL with hash routing for Telegram direct-opening
        let fullLink = link;
        if (typeof window !== 'undefined' && !fullLink.startsWith('http://') && !fullLink.startsWith('https://')) {
            const cleanPath = fullLink.startsWith('/') ? fullLink : `/${fullLink}`;
            fullLink = `${window.location.origin}/#${cleanPath}`;
        }

        const payload: Record<string, unknown> = {
            uid,
            body,
            sendAfter,
            link: fullLink
        };

        // Try sending with retry if server is still spinning up
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await fetch('/api/push', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8'
                    },
                    body: JSON.stringify(payload)
                });

                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    const data = await response.json();
                    if (!response.ok) {
                        console.warn(`[PUSH] Server push returned status ${response.status}:`, data?.error || data);
                        return null;
                    }
                    return data.id || null;
                }

                // If server returned non-JSON (e.g. cold-start HTML "Starting Server...")
                const responseText = await response.text();
                if (responseText.includes('Starting Server') || response.status === 502 || response.status === 503) {
                    if (attempt === 0) {
                        // Wait 1.5s and retry once
                        await new Promise(res => setTimeout(res, 1500));
                        continue;
                    }
                }
                
                console.warn(`[PUSH] Server returned non-JSON (${response.status}), push deferred.`);
                return null;
            } catch (fetchErr) {
                if (attempt === 0) {
                    await new Promise(res => setTimeout(res, 1500));
                    continue;
                }
                console.warn("[PUSH] Network error during push delivery:", fetchErr);
                return null;
            }
        }
        return null;
    } catch (err) {
        console.warn("[PUSH] Notification delivery deferred:", err);
        return null;
    }
};

export const cancelPushNotification = async (notificationId: string): Promise<void> => {
    try {
        await fetch(`/api/push/${notificationId}`, {
            method: 'DELETE'
        });
    } catch (err) {
        console.warn("Failed to cancel push notification", err);
    }
};

export const getBehavioralFeedback = async (clientId: string, onlyUnacknowledged = false): Promise<BehavioralFeedback[]> => {
    return retryOperation(async () => {
        let query = supabase.from('behavioral_feedback').select('*').eq('client_id', clientId);
        if (onlyUnacknowledged) query = query.is('acknowledged_at', null);
        const { data, error = null } = await query.order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []).map(f => ({
            id: f.id,
            clientId: f.client_id,
            type: f.type as FeedbackType,
            content: f.content,
            createdAt: f.created_at,
            acknowledgedAt: f.acknowledged_at
        }));
    });
};

export const addBehavioralFeedback = async (clientId: string, type: FeedbackType, content: string): Promise<BehavioralFeedback> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('behavioral_feedback').insert({ client_id: clientId, type, content, created_at: new Date().toISOString() }).select().single();
        if (error) throw error;
        await createAppNotification(clientId, `Die Göttin hat eine neue ${type} ausgesprochen.`, '/');
        return {
            id: data.id,
            clientId: data.client_id,
            type: data.type as FeedbackType,
            content: data.content,
            createdAt: data.created_at,
            acknowledgedAt: data.acknowledged_at
        };
    });
};

export const acknowledgeFeedback = async (feedbackId: string): Promise<void> => {
    return retryOperation(async () => {
        const { data: feedback, error: fetchError } = await supabase.from('behavioral_feedback').select('client_id, type').eq('id', feedbackId).single();
        if (fetchError) throw fetchError;

        const { error = null } = await supabase.from('behavioral_feedback').update({ acknowledged_at: new Date().toISOString() }).eq('id', feedbackId);
        if (error) throw error;

        // Notification Trigger
        try {
            const { data: client } = await supabase.from('clients').select('name').eq('id', feedback.client_id).single();
            const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', feedback.client_id).maybeSingle();
            let recipientId = convo?.domina_id;
            if (!recipientId) {
                recipientId = await getDominaId();
            }
            if (recipientId) {
                const msg = `${client?.name || 'Dein Klient'} hat die ${feedback.type} zur Kenntnis genommen.`;
                await createAppNotification(recipientId, msg, `/clients/${feedback.client_id}`);
            }
        } catch (e) {
            console.warn("Failed to send feedback acknowledgment notification", e);
        }
    });
};

export const deleteBehavioralFeedback = async (feedbackId: string): Promise<void> => {
    return retryOperation(async () => {
        const { error = null } = await supabase.from('behavioral_feedback').delete().eq('id', feedbackId);
        if (error) throw error;
    });
};

export const getDominaDashboardStats = async (): Promise<DominaDashboardStats> => {
    return retryOperation(async () => {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        
        // 1. First get active client IDs as they are needed for other queries
        const { data: activeClientsData = null } = await supabase.from('clients')
            .select('id')
            .eq('is_active', true)
            .eq('is_paused', false);
            
        const activeIds = (activeClientsData || []).map(c => c.id);
        const activeCount = activeIds.length;
        
        if (activeIds.length === 0) {
            return {
                activeClients: 0,
                overdueTasks: 0,
                monthlyTribute: {},
                upcomingDeadlines: [],
                recentActivities: [],
                recentReports: [],
                recentDiaryEntries: []
            };
        }

        const excludedStatuses = formatFilterList([TaskStatus.COMPLETED, TaskStatus.CLOSED_INCOMPLETE, TaskStatus.PENDING_REVIEW]);
        
        // 2. Parallelize independent queries
        const [overdueRes, paymentsRes, upcomingRes, recentTasksRes, recentDiaryRes, recentReportsRes] = await Promise.all([
            supabase.from('task_instances')
                .select('*', { count: 'exact', head: true })
                .in('client_id', activeIds)
                .lt('end_date_time', now.toISOString())
                .not('status', 'in', excludedStatuses),
            
            supabase.from('payments')
                .select('amount, currency')
                .gte('date', firstDayOfMonth),
            
            supabase.from('task_instances')
                .select('*, clients(name)')
                .in('client_id', activeIds)
                .in('category', [TaskCategory.SUB, TaskCategory.PERSONAL])
                .not('status', 'in', excludedStatuses)
                .order('end_date_time', { ascending: true })
                .limit(50),
            
            supabase.from('task_instances')
                .select('id, title, created_at, client_id, clients(name)')
                .in('client_id', activeIds)
                .order('created_at', { ascending: false })
                .limit(5),

            supabase.from('diary_entries')
                .select('*, clients:client_id(name)')
                .in('client_id', activeIds)
                .order('date', { ascending: false })
                .limit(5),

            supabase.from('report_submissions')
                .select('*, clients:client_id(name), report_templates:template_id(title)')
                .in('client_id', activeIds)
                .order('submitted_at', { ascending: false })
                .limit(5)
        ]);

        const monthlyTribute: { [key in Currency]?: number } = {};
        (paymentsRes.data || []).forEach((p: any) => { 
            const curr = normalizeCurrency(p.currency);
            monthlyTribute[curr] = (monthlyTribute[curr] || 0) + Number(p.amount); 
        });

        // Map reports to include virtual fields
        const recentReports = (recentReportsRes.data || []).map(s => {
            const clientData = getJoinedData(s, 'clients');
            const templateData = getJoinedData(s, 'report_templates');
            return {
                ...s,
                templateId: s.template_id,
                clientId: s.client_id,
                submittedAt: s.submitted_at,
                clientName: clientData?.name,
                templateTitle: templateData?.title
            } as ReportSubmission;
        });

        const recentDiaryEntries = (recentDiaryRes.data || []).map(d => {
            const clientData = getJoinedData(d, 'clients');
            return {
                ...mapDiaryEntryFromDB(d),
                clientName: clientData?.name
            } as DiaryEntry & { clientName?: string };
        });

        return { 
            activeClients: activeCount, 
            overdueTasks: overdueRes.count || 0, 
            monthlyTribute: monthlyTribute as any, 
            upcomingDeadlines: (upcomingRes.data || []).map(mapTaskFromDB), 
            recentActivities: (recentTasksRes.data || []).map(a => {
                const clientData = getJoinedData(a, 'clients');
                return { id: `task-${a.id}`, text: `${clientData?.name || 'Unbekannt'} wurde "${a.title}" zugewiesen.`, createdAt: a.created_at, link: `/clients/${a.client_id}?tab=tasks` };
            }),
            recentReports,
            recentDiaryEntries: recentDiaryEntries as DiaryEntry[]
        };
    });
};

export const getDailyReportForClient = async (clientId: string, dateStr: string): Promise<DailyReport> => {
    return retryOperation(async () => {
        // 1. Fetch Diary Entries for that day
        const { data: diaryData = [] } = await supabase.from('diary_entries')
            .select('*')
            .eq('client_id', clientId);
        
        const filteredDiaries = (diaryData || [])
            .map(mapDiaryEntryFromDB)
            .filter(d => d.date && d.date.startsWith(dateStr));

        // 2. Fetch Payments for that day
        const { data: paymentsData = [] } = await supabase.from('payments')
            .select('*')
            .eq('client_id', clientId);
        
        const filteredPayments = (paymentsData || [])
            .map(mapPaymentFromDB)
            .filter(p => p.date && p.date.startsWith(dateStr));

        // 3. Fetch Task Instances for that day
        const { data: tasksData = [] } = await supabase.from('task_instances')
            .select('*, clients(name, is_active, is_paused), task_labels(*)')
            .eq('client_id', clientId);

        const filteredTasks = (tasksData || [])
            .map(mapTaskFromDB)
            .filter(t => {
                const dueStr = t.endDateTime || t.startDateTime;
                return dueStr && dueStr.startsWith(dateStr);
            });

        // 4. Fetch Messages for that day
        const { data: convo } = await supabase.from('conversations')
            .select('id')
            .eq('client_id', clientId)
            .maybeSingle();
            
        let filteredMessages: Message[] = [];
        if (convo) {
            const { data: msgsData = [] } = await supabase.from('messages')
                .select('*')
                .eq('conversation_id', convo.id);
            
            filteredMessages = (msgsData || [])
                .map(mapMessageFromDB)
                .filter(m => m.timestamp && m.timestamp.startsWith(dateStr));
        }

        return {
            date: dateStr,
            clientId,
            diaryEntries: filteredDiaries,
            messages: filteredMessages,
            payments: filteredPayments,
            tasks: filteredTasks
        };
    });
};

export const getSubDashboardData = async (clientId: string): Promise<SubDashboardData> => {
    return retryOperation(async () => {
        // Clean up any accidentally pre-generated future pending rituals
        await cleanupFuturePreGeneratedRituals(clientId).catch(() => {});

        // Rituals are strictly generated on the CURRENT day (today at/after midnight), never in advance!
        const todayStr = toLocalDateStr(new Date());
        await autoGenerateDailyRituals(clientId, todayStr).catch(e => console.error("AutoGenerate failed", e));
        await autoGenerateFlashAlerts(clientId, todayStr).catch(e => console.error("Alert generation failed", e));
        
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const startOfTomorrow = new Date(tomorrow.setHours(0,0,0,0));
        const endOfTomorrow = new Date(tomorrow.setHours(23,59,59,999));

        const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
        const excludedList = [TaskStatus.COMPLETED, TaskStatus.CLOSED_INCOMPLETE].map(s => s.includes(' ') ? `"${s}"` : s).join(',');
        
        let userEmail = 'Unknown';
        
        // Retry logic for auth to handle hydration/lock races in iframes
        for (let i = 0; i < 3; i++) {
            const { data } = await supabase.auth.getUser();
            if (data.user?.email) {
                userEmail = data.user.email;
                break;
            }
            // Try getSession as fallback inside retry
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData.session?.user?.email) {
                userEmail = sessionData.session.user.email;
                console.log(`[API] getSubDashboardData: getUser() was null but getSession() found ${userEmail} (Attempt ${i+1})`);
                break;
            }
            if (i < 2) await new Promise(r => setTimeout(r, 200 * (i + 1)));
        }

        if (userEmail === 'Unknown' && typeof window !== 'undefined') {
            try {
                const cachedUser = JSON.parse(localStorage.getItem('fairyrealm_cached_user_session') || '{}');
                if (cachedUser.email) userEmail = cachedUser.email;
            } catch {
                // Ignore storage parse error
            }
        }

        const isDomina = userEmail !== 'Unknown' && DOMINA_EMAILS.includes(userEmail.toLowerCase());
        
        console.log(`[API] getSubDashboardData for client ${clientId}. Logged in as: ${userEmail} (Role: ${isDomina ? 'DOMINA' : 'SUB'})`);

        // Parallelize independent queries
        const [tasksRes, tomorrowTasksRes, diaryRes, clientRes, convoRes, paymentsRes] = await Promise.all([
            supabase.from('task_instances')
                .select('*')
                .eq('client_id', clientId)
                .or(`visibility_date.lte.${now.toISOString()},category.eq."${TaskCategory.PERSONAL}",category.eq."${TaskCategory.RITUAL}"`)
                .or(`status.not.in.(${excludedList}),completed_at.gte.${fortyEightHoursAgo.toISOString()}`),
            
            supabase.from('task_instances')
                .select('*')
                .eq('client_id', clientId)
                .gte('end_date_time', startOfTomorrow.toISOString())
                .lte('end_date_time', endOfTomorrow.toISOString()),

            supabase.from('diary_entries')
                .select('*')
                .eq('client_id', clientId)
                .not('goddess_feedback', 'is', null)
                .order('date', { ascending: false })
                .limit(1)
                .maybeSingle(),
            
            supabase.from('clients')
                .select('*')
                .eq('id', clientId)
                .maybeSingle(),
            
            supabase.from('conversations')
                .select('id')
                .eq('client_id', clientId)
                .maybeSingle(),
            
            supabase.from('payments')
                .select('*')
                .eq('client_id', clientId)
                .order('date', { ascending: false })
                .limit(20) // Optimization: Limit payments to recent 20
        ]);

        if (tasksRes.error) throw tasksRes.error;
        
        let clientData = clientRes.data;

        // Fallback 1: Query by email if ID query returned empty (e.g. ID mismatch or email-based RLS)
        if (!clientData && userEmail !== 'Unknown') {
            try {
                const { data: clientByEmail } = await supabase.from('clients').select('*').eq('email', userEmail.toLowerCase()).maybeSingle();
                if (clientByEmail) {
                    clientData = clientByEmail;
                }
            } catch {
                // Ignore fallback lookup error
            }
        }

        // Fallback 2: Check local client cache
        if (!clientData && typeof window !== 'undefined') {
            try {
                const localProfile = localStorage.getItem(`fairyrealm_client_profile_${clientId}`);
                if (localProfile) clientData = JSON.parse(localProfile);
            } catch {
                // Ignore cache parse error
            }
        } else if (clientData && typeof window !== 'undefined') {
            try {
                localStorage.setItem(`fairyrealm_client_profile_${clientId}`, JSON.stringify(clientData));
            } catch {
                // Ignore cache write error
            }
        }

        // Fallback 3: Construct minimal client object to prevent fatal crashes
        if (!clientData) {
            console.warn(`[API] getSubDashboardData: Using resilient client profile fallback for ${clientId} (${userEmail})`);
            clientData = {
                id: clientId,
                name: userEmail !== 'Unknown' ? userEmail.split('@')[0] : 'Klient',
                email: userEmail !== 'Unknown' ? userEmail : '',
                is_active: true,
                created_at: new Date().toISOString()
            };
        }

        // Dependent queries
        let rulebook = null;
        if (clientData.rulebook_id) {
            const { data } = await supabase.from('rulebooks').select('*').eq('id', clientData.rulebook_id).maybeSingle();
            rulebook = data;
        }

        let latestMessage = null;
        if (convoRes.data) {
            const { data } = await supabase.from('messages')
                .select('*')
                .eq('conversation_id', convoRes.data.id)
                .eq('sender', UserRole.DOMINA)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (data) latestMessage = mapMessageFromDB(data);
        }

        return { 
            todaysTasks: (tasksRes.data || []).map(mapTaskFromDB), 
            tomorrowTasks: (tomorrowTasksRes.data || []).map(mapTaskFromDB),
            latestFeedback: diaryRes.data ? mapDiaryEntryFromDB(diaryRes.data) : null, 
            rulebook, 
            latestMessage, 
            payments: (paymentsRes.data || []).map(mapPaymentFromDB), 
            client: mapClientFromDB(clientData) 
        };
    });
};

export const getDominaStatus = async (): Promise<DominaStatusRecord | null> => {
    return retryOperation(async () => {
        const { data = null, error = null } = await supabase.from('domina_status').select('*').eq('id', 1).maybeSingle();
        if (error) throw error;
        return data ? { 
            id: data.id, 
            status: data.status as DominaStatus, 
            comment: data.comment, 
            updatedAt: data.updated_at,
            telegramChatId: data.telegram_chat_id
        } : null;
    });
};

export const setDominaStatus = async (status: DominaStatus, comment?: string): Promise<DominaStatusRecord> => {
    return retryOperation(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        const upsertData: { id: number, status: DominaStatus, comment: string, updated_at: string, domina_id?: string } = { id: 1, status, comment: comment || '', updated_at: new Date().toISOString() };
        if (user) upsertData.domina_id = user.id;
        
        const { data = null, error = null } = await supabase.from('domina_status').upsert(upsertData).select().single();
        if (error) throw error;
        return { id: data.id, status: data.status as DominaStatus, comment: data.comment, updatedAt: data.updated_at };
    });
};

export const getRulebooks = async (): Promise<Rulebook[]> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('rulebooks').select('*').order('title');
        if (error) throw error;
        return data || [];
    });
};

export const getRulebookById = async (id: string): Promise<Rulebook | null> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('rulebooks').select('*').eq('id', id).maybeSingle();
        if (error) return null;
        return data;
    });
};

export const addRulebook = async (rulebook: Omit<Rulebook, 'id'>): Promise<Rulebook> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('rulebooks').insert(rulebook).select().single();
        if (error) throw error;
        return data;
    });
};

export const updateRulebook = async (id: string, updates: Partial<Rulebook>): Promise<Rulebook> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('rulebooks').update(updates).eq('id', id).select().single();
        if (error) throw error;
        return data;
    });
};

export const deleteRulebook = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        await supabase.from('rulebooks').delete().eq('id', id);
        const { data: allClients } = await supabase.from('clients').select('id, profile, rulebook_id');
        if (allClients) {
            for (const cl of allClients) {
                const profile = cl.profile || {};
                const currentRbs: string[] = Array.isArray(profile.rulebookIds)
                    ? profile.rulebookIds
                    : (cl.rulebook_id ? [cl.rulebook_id] : []);
                if (currentRbs.includes(id) || cl.rulebook_id === id) {
                    const updatedRbs = currentRbs.filter((rid: string) => rid !== id);
                    await supabase.from('clients').update({
                        rulebook_id: updatedRbs[0] || null,
                        profile: { ...profile, rulebookIds: updatedRbs }
                    }).eq('id', cl.id);
                }
            }
        }
    });
};

export const assignRulebookToClients = async (rulebookId: string, clientIds: string[]): Promise<void> => {
    await retryOperation(async () => {
        const { data: allClients } = await supabase.from('clients').select('id, profile, rulebook_id');
        if (allClients) {
            for (const cl of allClients) {
                const isSelected = clientIds.includes(cl.id);
                const profile = cl.profile || {};
                const existingRbs: string[] = Array.isArray(profile.rulebookIds)
                    ? profile.rulebookIds
                    : (cl.rulebook_id ? [cl.rulebook_id] : []);

                const hasRulebook = existingRbs.includes(rulebookId);

                if (isSelected && !hasRulebook) {
                    const updatedRbs = Array.from(new Set([...existingRbs, rulebookId]));
                    await supabase.from('clients').update({
                        rulebook_id: updatedRbs[0] || null,
                        profile: { ...profile, rulebookIds: updatedRbs }
                    }).eq('id', cl.id);
                } else if (!isSelected && hasRulebook) {
                    const updatedRbs = existingRbs.filter((id: string) => id !== rulebookId);
                    await supabase.from('clients').update({
                        rulebook_id: updatedRbs[0] || null,
                        profile: { ...profile, rulebookIds: updatedRbs }
                    }).eq('id', cl.id);
                }
            }
        }
    });
};

export const cleanupFuturePreGeneratedRituals = async (clientId?: string): Promise<void> => {
    return retryOperation(async () => {
        const now = new Date();
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        
        let query = supabase.from('task_instances')
            .delete()
            .eq('category', TaskCategory.RITUAL)
            .gt('start_date_time', endOfToday.toISOString())
            .in('status', [TaskStatus.PENDING]);

        if (clientId) {
            query = query.eq('client_id', clientId);
        }

        const { error } = await query;
        if (error) {
            console.warn('[Cleanup] Error pruning future pre-generated rituals:', error.message);
        }
    }).catch(e => {
        console.warn('[Cleanup] Exception in cleanupFuturePreGeneratedRituals:', e);
    });
};

export const cleanupOverdueRituals = async (clientId: string): Promise<void> => {
    return retryOperation(async () => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const { data: overdue = null } = await supabase.from('task_instances').select('id').eq('client_id', clientId).eq('category', TaskCategory.RITUAL).lt('end_date_time', startOfToday.toISOString()).in('status', [TaskStatus.PENDING, TaskStatus.IN_PROGRESS]);
        if (overdue?.length) await supabase.from('task_instances').update({ status: TaskStatus.CLOSED_INCOMPLETE, goddess_notes: 'Nicht erledigt.' }).in('id', overdue.map(r => r.id));
    });
};

const activeGenerations = new Set<string>();
const generatedDaysCache = new Set<string>();

export const clearGeneratedDaysCache = (clientId?: string) => {
    if (clientId) {
        for (const key of Array.from(generatedDaysCache.keys())) {
            if (key.startsWith(`${clientId}:`)) {
                generatedDaysCache.delete(key);
            }
        }
    } else {
        generatedDaysCache.clear();
    }
};

export const autoGenerateDailyRituals = async (clientId: string, selectedDateStr?: string): Promise<void> => {
    const now = new Date();
    const todayStr = toLocalDateStr(now);
    const dateStr = selectedDateStr || todayStr;

    // HARD GUARD: Ritual tasks are strictly generated on the CURRENT day (at/after midnight).
    // They are NEVER pre-generated for future days.
    if (dateStr > todayStr) {
        return;
    }

    const lockKey = `${clientId}:${dateStr}`;

    if (generatedDaysCache.has(lockKey)) {
        return;
    }

    if (activeGenerations.has(lockKey)) {
        console.log(`[Lock] Already generating for ${lockKey}. Skipping concurrent generation.`);
        return;
    }

    activeGenerations.add(lockKey);

    try {
        await retryOperation(async () => {
            // PRE-CHECK: only generate for active and unpaused clients
            const { data: clientCheck } = await supabase.from('clients')
                .select('is_active, is_paused')
                .eq('id', clientId)
                .maybeSingle();
            
            if (!clientCheck || !clientCheck.is_active || clientCheck.is_paused) {
                generatedDaysCache.add(lockKey);
                return;
            }

            const startOfDay = new Date(`${dateStr}T00:00:00`);
            const endOfDay = new Date(`${dateStr}T23:59:59.999`);
            
            // 1. Get only relevant templates
            const { data: templates = null } = await supabase.from('task_templates')
                .select('*')
                .or(`category.eq."${TaskCategory.RITUAL}",recurrence.not.is.null`);
            if (!templates) return;
            
            const userTemplates = templates.filter(t => t.assigned_client_ids && t.assigned_client_ids.includes(clientId));
            if (userTemplates.length === 0) return;

            // 1b. Optimization: Instead of fetching ALL instances, just fetch existing for TODAY and potentially first instance for recurrence.
            // We actually only need the first instance to determine the "anchor" for weekly/monthly logic.
            // Let's fetch only ONE instance per template_id (the first one).
            
            // We'll parallelize today's check and the anchor check
            const [todayRes, anchorRes] = await Promise.all([
                supabase.from('task_instances')
                    .select('template_id, title')
                    .eq('client_id', clientId)
                    .gte('start_date_time', startOfDay.toISOString())
                    .lte('start_date_time', endOfDay.toISOString()),
                
                // To get anchor efficiently, we could fetch some but not ALL. 
                // Actually, for better performance, we'll fetch the first instance of each relevant template
                supabase.from('task_instances')
                    .select('template_id, start_date_time')
                    .eq('client_id', clientId)
                    .in('template_id', userTemplates.map(t => t.id))
                    .order('start_date_time', { ascending: true })
            ]);

            const firstInstanceMap = new Map<string, Date>();
            if (anchorRes.data) {
                anchorRes.data.forEach((fi) => {
                    if (!firstInstanceMap.has(fi.template_id)) {
                        firstInstanceMap.set(fi.template_id, new Date(fi.start_date_time));
                    }
                });
            }
            
            const existingData = todayRes.data || [];
            const existingTemplateIds = new Set(existingData.map(e => e.template_id));
            const existingTitles = new Set(existingData.map(e => e.title.trim().toLowerCase()));

            const toInsert = [];
            const currentTargetDate = startOfDay;

            for (const tmpl of userTemplates) {
                const hashTitle = tmpl.title.trim().toLowerCase();
                if (existingTemplateIds.has(tmpl.id) || existingTitles.has(hashTitle)) continue;
                
                // Recurrence Logic
                let shouldGenerate = false;
                const rec = tmpl.recurrence || RecurrenceType.DAILY;
                
                // Recurrence anchor: prioritize the template's configured start_date, fallback to client first instance or start of day
                const clientFirstInstDate = firstInstanceMap.get(tmpl.id);
                const tmplStart = tmpl.start_date
                    ? parseLocalDate(tmpl.start_date)
                    : (clientFirstInstDate
                        ? new Date(clientFirstInstDate.getFullYear(), clientFirstInstDate.getMonth(), clientFirstInstDate.getDate())
                        : new Date(startOfDay.getFullYear(), startOfDay.getMonth(), startOfDay.getDate())); 
                
                // Only generate if we are on or past the start date
                if (currentTargetDate < tmplStart) {
                    continue;
                }

                if (rec === RecurrenceType.DAILY) {
                    shouldGenerate = true;
                } else if (rec === RecurrenceType.WEEKLY) {
                    if (currentTargetDate.getDay() === tmplStart.getDay()) shouldGenerate = true;
                } else if (rec === RecurrenceType.MONTHLY) {
                    if (currentTargetDate.getDate() === tmplStart.getDate()) shouldGenerate = true;
                } else if (rec === RecurrenceType.YEARLY) {
                    if (currentTargetDate.getDate() === tmplStart.getDate() && currentTargetDate.getMonth() === tmplStart.getMonth()) shouldGenerate = true;
                } else if (rec === RecurrenceType.CUSTOM) {
                    let customDays: number[] | null = null;
                    let customInterval: number | null = null;
                    if (tmpl.goddess_notes && typeof tmpl.goddess_notes === 'string' && tmpl.goddess_notes.startsWith('__COACHING_META__')) {
                        try {
                            const meta = JSON.parse(tmpl.goddess_notes.replace('__COACHING_META__', ''));
                            if (Array.isArray(meta.customDays) && meta.customDays.length > 0) {
                                customDays = meta.customDays;
                            }
                            if (typeof meta.customInterval === 'number' && meta.customInterval > 0) {
                                customInterval = meta.customInterval;
                            }
                        } catch {
                            // ignore
                        }
                    }
                    if (customDays && customDays.length > 0) {
                        if (customDays.includes(currentTargetDate.getDay())) {
                            shouldGenerate = true;
                        }
                    } else if (customInterval && customInterval > 1) {
                        const diffTime = currentTargetDate.getTime() - tmplStart.getTime();
                        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays >= 0 && diffDays % customInterval === 0) {
                            shouldGenerate = true;
                        }
                    } else {
                        shouldGenerate = true;
                    }
                } else {
                    shouldGenerate = true; // Fallback for customized / missing rules
                }
                
                // End Date: ensure recurrence_end_date or end_date is respected
                const effectiveEndDateStr = tmpl.recurrence_end_date || tmpl.end_date;
                if (shouldGenerate && effectiveEndDateStr) {
                    const recEndDate = new Date(`${effectiveEndDateStr}T23:59:59.999`);
                    if (currentTargetDate > recEndDate) {
                        shouldGenerate = false;
                    }
                }

                if (shouldGenerate) {
                    const dTime = tmpl.deadline_time || '23:59';
                    const formattedDTime = dTime.length === 5 ? `${dTime}:00` : dTime;
                    let taskEndDateTime = new Date(`${dateStr}T${formattedDTime}`);
                    if (taskEndDateTime.getTime() < startOfDay.getTime()) {
                        taskEndDateTime = new Date(`${dateStr}T23:59:59`);
                    }

                    let taskVisibilityDate = new Date(startOfDay);
                    const preset = normalizeVisibilityPreset(tmpl.visibility_preset);
                    if (preset === TaskVisibilityPreset.H24_BEFORE) {
                        taskVisibilityDate = new Date(taskEndDateTime.getTime() - 24 * 60 * 60 * 1000);
                    } else if (preset === TaskVisibilityPreset.D7_BEFORE) {
                        taskVisibilityDate = new Date(taskEndDateTime.getTime() - 7 * 24 * 60 * 60 * 1000);
                    } else if (preset === TaskVisibilityPreset.CUSTOM && tmpl.custom_visibility_date) {
                        if (tmpl.custom_visibility_date.includes('T')) {
                            const timePart = tmpl.custom_visibility_date.split('T')[1].substring(0, 5);
                            taskVisibilityDate = new Date(`${dateStr}T${timePart}:00`);
                        } else {
                            taskVisibilityDate = new Date(tmpl.custom_visibility_date);
                        }
                    }

                    toInsert.push({
                        client_id: clientId,
                        template_id: tmpl.id,
                        title: tmpl.title,
                        description: tmpl.description || '',
                        category: tmpl.category,
                        type: tmpl.type,
                        status: TaskStatus.PENDING,
                        start_date_time: startOfDay.toISOString(),
                        end_date_time: taskEndDateTime.toISOString(),
                        visibility_date: taskVisibilityDate.toISOString(),
                        review_required: tmpl.review_required || false,
                        is_report_required: tmpl.is_report_required || false,
                        is_proof_required: tmpl.is_proof_required || false,
                        duration_target: tmpl.duration_target,
                        count_target: tmpl.count_target,
                        interval_config: tmpl.interval_config || [],
                        attachment_link: tmpl.attachment_link,
                        goddess_notes: tmpl.goddess_notes,
                        goddess_reminders: tmpl.goddess_reminders || [],
                        label_id: tmpl.label_id,
                        is_multi_day: false,
                        created_at: now.toISOString(),
                        updated_at: now.toISOString(),
                        comments: []
                    });
                }
            }
            
            if (toInsert.length > 0) {
                // Final check to prevent race conditions: double check if any of these were inserted while we were looping
                const { data: finalCheck = null } = await supabase.from('task_instances')
                    .select('template_id, title')
                    .eq('client_id', clientId)
                    .gte('start_date_time', startOfDay.toISOString())
                    .lte('start_date_time', endOfDay.toISOString());
                
                const existingInFinal = new Set(finalCheck?.map(f => f.template_id) || []);
                const existingTitlesInFinal = new Set(finalCheck?.map(f => (f.title || '').trim().toLowerCase()) || []);
                const trulyNewToInsert = toInsert.filter(task => 
                    !existingInFinal.has(task.template_id) && 
                    !existingTitlesInFinal.has((task.title || '').trim().toLowerCase())
                );

                if (trulyNewToInsert.length > 0) {
                    const { error: insertError } = await supabase.from('task_instances').insert(trulyNewToInsert);
                    if (insertError) throw insertError;
                }
            }
            generatedDaysCache.add(lockKey);
        });
    } finally {
        activeGenerations.delete(lockKey);
    }
};

export const hasReportedSickToday = async (clientId: string): Promise<boolean> => {
    return retryOperation(async () => {
        const today = toLocalDateStr(new Date());
        const { count = null } = await supabase.from('daily_checklists').select('*', { count: 'exact', head: true }).eq('client_id', clientId).eq('date', today).eq('is_sick_day', true);
        if ((count || 0) > 0) return true;
        const { count: diaryCount = null } = await supabase.from('diary_entries').select('*', { count: 'exact', head: true }).eq('client_id', clientId).eq('type', 'SICK_DAY').gte('date', `${today}T00:00:00`).lte('date', `${today}T23:59:59`);
        return (diaryCount || 0) > 0;
    });
};

export const reportSickDay = async (clientId: string, clientName: string): Promise<void> => {
    return retryOperation(async () => {
        const now = new Date();
        const todayStr = toLocalDateStr(now);
        await addDiaryEntry({ clientId, author: 'SUB', date: now.toISOString(), type: 'SICK_DAY', content: `${clientName} hat sich für heute krankgemeldet.` });
        const { data: tasks = null } = await supabase.from('task_instances').select('id').eq('client_id', clientId).eq('status', TaskStatus.PENDING).gte('end_date_time', `${todayStr}T00:00:00`).lte('end_date_time', `${todayStr}T23:59:59`);
        if (tasks?.length) await supabase.from('task_instances').update({ status: TaskStatus.CLOSED_INCOMPLETE, goddess_notes: 'Krankmeldung.' }).in('id', tasks.map(t => t.id));
    });
};

export const getSickClientIdsForToday = async (): Promise<string[]> => {
    return retryOperation(async () => {
        const today = toLocalDateStr(new Date());
        const { data = null } = await supabase.from('diary_entries').select('client_id').eq('type', 'SICK_DAY').gte('date', `${today}T00:00:00`).lte('date', `${today}T23:59:59`);
        return [...new Set((data as any[] || []).map((entry: any) => entry.client_id as string))];
    });
};

export const resetConversation = async (conversationId: string): Promise<void> => {
    await retryOperation(async () => { await supabase.from('messages').delete().eq('conversation_id', conversationId); await supabase.from('conversations').delete().eq('id', conversationId); });
};

export const toggleArchiveConversation = async (conversationId: string, isArchived: boolean): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('conversations').update({ is_archived: isArchived }).eq('id', conversationId);
        if (error) throw error;
    });
};

export const toggleMessageReaction = async (messageId: string, emoji: string, userId: string): Promise<void> => {
    await retryOperation(async () => {
        const { data: msg = null } = await supabase.from('messages').select('reactions').eq('id', messageId).single();
        const reactions: { emoji: string, userIds: string[] }[] = msg?.reactions || [];
        const reactionIndex = reactions.findIndex(r => r.emoji === emoji);
        if (reactionIndex > -1) {
            const userIndex = reactions[reactionIndex].userIds.indexOf(userId);
            if (userIndex > -1) { reactions[reactionIndex].userIds.splice(userIndex, 1); if (!reactions[reactionIndex].userIds.length) reactions.splice(reactionIndex, 1); } else reactions[reactionIndex].userIds.push(userId);
        } else reactions.push({ emoji, userIds: [userId] });
        await supabase.from('messages').update({ reactions }).eq('id', messageId);
    });
};

export const updateMessage = async (messageId: string, newContent: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('messages').update({ content: newContent, is_edited: true }).eq('id', messageId);
        if (error) throw error;
    });
};

export const deleteMessage = async (messageId: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('messages').delete().eq('id', messageId);
        if (error) throw error;
    });
};

export const addTaskComment = async (taskId: string, comment: Omit<Comment, 'id' | 'createdAt'>): Promise<TaskInstance> => {
    return retryOperation(async () => {
        const { data: task = null } = await supabase.from('task_instances').select('comments').eq('id', taskId).single();
        const comments = task?.comments || [];
        const newComment = { ...comment, id: uuid(), createdAt: new Date().toISOString() };
        const { data = null, error = null } = await supabase.from('task_instances').update({ comments: [...comments, newComment] }).eq('id', taskId).select('*, clients(name), task_labels(*)').single();
        if (error) throw error;
        return mapTaskFromDB(data);
    });
};

export const addAttachmentMessage = async (payload: SendMessagePayload, file: File, clientId: string): Promise<SendMessagePayload> => {
    return retryOperation(async () => {
        const fn = `chat/${clientId}/${uuid()}.${file.name.split('.').pop()}`;
        await supabase.storage.from('media').upload(fn, file);
        const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fn);
        const content = JSON.parse(payload.content); if (!content.file) content.file = {}; content.file.url = publicUrl;
        return { ...payload, content: JSON.stringify(content) };
    });
};

export const addAudioMessage = async (payload: SendMessagePayload, audioBlob: Blob, clientId: string): Promise<SendMessagePayload> => {
    return retryOperation(async () => {
        const fn = `chat/${clientId}/audio-${uuid()}.webm`;
        await supabase.storage.from('media').upload(fn, audioBlob);
        const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fn);
        return { ...payload, audioUrl: publicUrl };
    });
};

export const getFolders = async (): Promise<Folder[]> => { return retryOperation(async () => { const { data = null } = await supabase.from('media_folders').select('*').order('name'); return (data || []).map(f => ({ ...f, assignedClientIds: f.assigned_client_ids as string[] | null })); }); };
export const getFiles = async (fid: string | null, includeAudioInputs: boolean = false): Promise<FileItem[]> => { return retryOperation(async () => { let q = supabase.from('media_files').select('*').order('created_at', { ascending: false }); if (!includeAudioInputs) { q = q.neq('type', 'AUDIO_INPUT'); } if (fid) q = q.eq('folder_id', fid); else q = q.is('folder_id', null); const { data = null } = await q; return (data || []).map(f => ({ ...f, folderId: f.folder_id, assignedClientIds: f.assigned_client_ids as string[] | null, createdAt: f.created_at })); }); };

const mapDailyAudioInputFromDB = (data: any): DailyAudioInput => {
    let meta: any = {};
    try {
        meta = JSON.parse(data.name);
    } catch (e) {
        meta = { title: data.name || 'Unbenanntes Audio-Paket', audios: [] };
    }
    return {
        id: data.id,
        title: meta.title || 'Unbenanntes Audio-Paket',
        audios: meta.audios || [],
        assignedClientIds: data.assigned_client_ids || null,
        periodType: meta.periodType || AudioPeriodType.DATE_RANGE,
        startDate: meta.startDate || null,
        endDate: meta.endDate || null,
        calendarWeeks: meta.calendarWeeks || null,
        intervalType: meta.intervalType || AudioIntervalType.EVERY_DAY,
        intervalDays: meta.intervalDays || 1,
        createdAt: data.created_at,
        isTemplate: meta.isTemplate || false,
    };
};

export const getDailyAudioInputs = async (): Promise<DailyAudioInput[]> => {
    return retryOperation(async () => {
        const { data = null } = await supabase.from('media_files').select('*').eq('type', 'AUDIO_INPUT').order('created_at', { ascending: false });
        return (data || []).map(mapDailyAudioInputFromDB);
    });
};

export const createDailyAudioInput = async (input: Omit<DailyAudioInput, 'id' | 'createdAt'>): Promise<DailyAudioInput> => {
    return retryOperation(async () => {
        const dbName = JSON.stringify({
            title: input.title,
            audios: input.audios,
            periodType: input.periodType,
            startDate: input.startDate,
            endDate: input.endDate,
            calendarWeeks: input.calendarWeeks,
            intervalType: input.intervalType,
            intervalDays: input.intervalDays,
            isTemplate: input.isTemplate || false,
        });

        const { data, error = null } = await supabase.from('media_files').insert({
            name: dbName,
            url: 'AUDIO_INPUT_METADATA',
            type: 'AUDIO_INPUT',
            folder_id: null,
            assigned_client_ids: input.assignedClientIds && input.assignedClientIds.length > 0 ? input.assignedClientIds : null
        }).select().single();

        if (error) throw error;
        return mapDailyAudioInputFromDB(data);
    });
};

export const updateDailyAudioInput = async (id: string, input: Partial<DailyAudioInput>): Promise<DailyAudioInput> => {
    return retryOperation(async () => {
        const { data: current, error: fetchError } = await supabase.from('media_files').select('*').eq('id', id).single();
        if (fetchError) throw fetchError;

        const currentMapped = mapDailyAudioInputFromDB(current);
        const updatedTitle = input.title !== undefined ? input.title : currentMapped.title;
        const updatedAudios = input.audios !== undefined ? input.audios : currentMapped.audios;
        const updatedPeriodType = input.periodType !== undefined ? input.periodType : currentMapped.periodType;
        const updatedStartDate = input.startDate !== undefined ? input.startDate : currentMapped.startDate;
        const updatedEndDate = input.endDate !== undefined ? input.endDate : currentMapped.endDate;
        const updatedCalendarWeeks = input.calendarWeeks !== undefined ? input.calendarWeeks : currentMapped.calendarWeeks;
        const updatedIntervalType = input.intervalType !== undefined ? input.intervalType : currentMapped.intervalType;
        const updatedIntervalDays = input.intervalDays !== undefined ? input.intervalDays : currentMapped.intervalDays;
        const updatedAssignedClientIds = input.assignedClientIds !== undefined ? input.assignedClientIds : currentMapped.assignedClientIds;
        const updatedIsTemplate = input.isTemplate !== undefined ? input.isTemplate : currentMapped.isTemplate;

        const dbName = JSON.stringify({
            title: updatedTitle,
            audios: updatedAudios,
            periodType: updatedPeriodType,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
            calendarWeeks: updatedCalendarWeeks,
            intervalType: updatedIntervalType,
            intervalDays: updatedIntervalDays,
            isTemplate: updatedIsTemplate || false,
        });

        const { data, error = null } = await supabase.from('media_files').update({
            name: dbName,
            assigned_client_ids: updatedAssignedClientIds && updatedAssignedClientIds.length > 0 ? updatedAssignedClientIds : null
        }).eq('id', id).select().single();

        if (error) throw error;
        return mapDailyAudioInputFromDB(data);
    });
};

export const deleteDailyAudioInput = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error = null } = await supabase.from('media_files').delete().eq('id', id);
        if (error) throw error;
    });
};
export const addFolder = async (name: string, aids: string[], fid: string | null = null): Promise<Folder> => { 
    return retryOperation(async () => { 
        const { data, error = null } = await supabase.from('media_folders').insert({ 
            name, 
            assigned_client_ids: aids.length ? aids : null,
            parent_id: fid
        }).select().single(); 
        if (error) throw error; 
        return { ...data, assignedClientIds: data.assigned_client_ids, parentId: data.parent_id }; 
    }); 
};
export const renameFolder = async (id: string, name: string): Promise<void> => { await retryOperation(async () => { const { error = null } = await supabase.from('media_folders').update({ name }).eq('id', id); if (error) throw error; }); };
export const renameFile = async (id: string, name: string): Promise<void> => { await retryOperation(async () => { const { error = null } = await supabase.from('media_files').update({ name }).eq('id', id); if (error) throw error; }); };
export const deleteFolder = async (id: string): Promise<void> => { 
    await retryOperation(async () => { 
        const { data } = await supabase.from('media_files').select('id').eq('folder_id', id); 
        if (data && data.length > 0) {
            await supabase.from('media_files').delete().in('id', data.map(f => f.id)); 
        }
        await supabase.from('media_folders').delete().eq('id', id); 
    }); 
};
export const addFile = async (file: File, name: string, fid: string | null, aids: string[]): Promise<FileItem> => { return retryOperation(async () => { const fn = `${uuid()}.${file.name.split('.').pop()}`; await supabase.storage.from('media').upload(fn, file); const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fn); const { data, error = null } = await supabase.from('media_files').insert({ name, url: publicUrl, type: file.type.startsWith('image/') ? 'IMAGE' : file.type === 'application/pdf' ? 'PDF' : 'FILE', folder_id: fid, assigned_client_ids: aids.length ? aids : null }).select().single(); if (error) throw error; return { ...data, folderId: data.folder_id, assignedClientIds: data.assigned_client_ids, createdAt: data.created_at }; }); };
export const addLink = async (name: string, url: string, fid: string | null, aids: string[]): Promise<FileItem> => { return retryOperation(async () => { const { data, error = null } = await supabase.from('media_files').insert({ name, url, type: 'LINK', folder_id: fid, assigned_client_ids: aids.length ? aids : null }).select().single(); if (error) throw error; return { ...data, folderId: data.folder_id, assignedClientIds: data.assigned_client_ids, createdAt: data.created_at }; }); };
export const deleteFile = async (id: string): Promise<void> => { await retryOperation(async () => { const { error = null } = await supabase.from('media_files').delete().eq('id', id); if (error) throw error; }); };
export const uploadProofFile = async (file: File, cid: string): Promise<{ publicUrl: string }> => { return retryOperation(async () => { const fn = `proofs/${cid}/${uuid()}.${file.name.split('.').pop()}`; await supabase.storage.from('media').upload(fn, file); const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fn); return { publicUrl }; }); };

export const getChecklistForClient = async (cid: string): Promise<DailyChecklist> => { return retryOperation(async () => { const t = toLocalDateStr(new Date()); const { data = null } = await supabase.from('daily_checklists').select('*').eq('client_id', cid).eq('date', t).maybeSingle(); if (data) return { id: data.id, clientId: data.client_id, date: data.date, items: data.items }; const { data: defs = [] } = await supabase.from('default_checklist_items').select('*').order('sort_order'); const its = (defs || []).filter((i: any) => !i.assigned_client_ids || i.assigned_client_ids.includes(cid)).map((i: any) => ({ id: uuid(), text: i.text, checked: false })); const { data: newList = null, error = null } = await supabase.from('daily_checklists').insert({ client_id: cid, date: t, items: its }).select().single(); if (error) throw error; return { id: newList.id, clientId: newList.client_id, date: newList.date, items: newList.items }; }); };
export const updateChecklist = async (id: string, its: any[]): Promise<void> => {
    await retryOperation(async () => {
        const { data: checklist, error: fetchError } = await supabase.from('daily_checklists').select('client_id').eq('id', id).single();
        if (fetchError) throw fetchError;

        const { error = null } = await supabase.from('daily_checklists').update({ items: its }).eq('id', id);
        if (error) throw error;

        // Notification Trigger (Notify Domina if all items are checked)
        const allChecked = its.every(i => i.checked);
        if (allChecked && its.length > 0) {
            try {
                const { data: client } = await supabase.from('clients').select('name').eq('id', checklist.client_id).single();
                const { data: convo } = await supabase.from('conversations').select('domina_id').eq('client_id', checklist.client_id).maybeSingle();
                let recipientId = convo?.domina_id;
                if (!recipientId) {
                    recipientId = await getDominaId();
                }
                if (recipientId) {
                    const msg = `${client?.name || 'Dein Klient'} hat seine tägliche Checkliste vollständig abgearbeitet.`;
                    await createAppNotification(recipientId, msg, `/clients/${checklist.client_id}`);
                }
            } catch (e) {
                console.warn("Failed to send checklist completion notification", e);
            }
        }
    });
};
export const getDefaultChecklistItems = async (): Promise<DefaultChecklistItem[]> => { return retryOperation(async () => { const { data = null } = await supabase.from('default_checklist_items').select('*').order('sort_order'); return (data || []).map(i => ({ ...i, sortOrder: i.sort_order, assignedClientIds: i.assigned_client_ids })); }); };
export const addDefaultChecklistItem = async (i: Omit<DefaultChecklistItem, 'id'>): Promise<DefaultChecklistItem> => { return retryOperation(async () => { const { data, error = null } = await supabase.from('default_checklist_items').insert({ text: i.text, sort_order: i.sortOrder, assigned_client_ids: i.assignedClientIds }).select().single(); if (error) throw error; return { ...data, sortOrder: data.sort_order, assignedClientIds: data.assigned_client_ids }; }); };
export const updateDefaultChecklistItem = async (id: string, updates: Partial<DefaultChecklistItem>): Promise<void> => { return retryOperation(async () => { const db: any = {}; if (updates.text !== undefined) db.text = updates.text; if (updates.sortOrder !== undefined) db.sort_order = updates.sortOrder; if (updates.assignedClientIds !== undefined) db.assigned_client_ids = updates.assignedClientIds; await supabase.from('default_checklist_items').update(db).eq('id', id); }); };
export const deleteDefaultChecklistItem = async (id: string): Promise<void> => { await retryOperation(async () => { const { error = null } = await supabase.from('default_checklist_items').delete().eq('id', id); if (error) throw error; }); };

export const deletePushSubscription = async (): Promise<void> => {
    // OneSignal handles this automatically
};

export const getAppNotifications = async (uid: string): Promise<AppNotification[]> => {
    let targetUid = uid;
    if (targetUid === 'domina') {
        const dId = await getDominaId();
        if (dId) targetUid = dId;
        else return [];
    }
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(targetUid);
    if (!isUUID) return [];

    return retryOperation(async () => {
        const { data = null } = await supabase
            .from('app_notifications')
            .select('*')
            .eq('user_id', targetUid)
            .order('created_at', { ascending: false })
            .limit(50);
        return data || [];
    });
};
const recentlyNotifiedDeadlines = new Set<string>();

export const checkAndNotifyTaskDeadlines = async (
    role?: UserRole, 
    currentUserIdOrClientId?: string
): Promise<void> => {
    try {
        const now = new Date();
        const dueSoonWindow = new Date(now.getTime() + 3 * 60 * 60 * 1000); // next 3 hours
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();

        const isDomina = role === UserRole.DOMINA;
        let dominaId: string | null = null;
        try {
            dominaId = await getDominaId();
        } catch {
            // fallback
        }

        // 1. Fetch active, unpaused clients
        const { data: clientsData } = await supabase
            .from('clients')
            .select('id, name')
            .eq('is_active', true)
            .eq('is_paused', false);

        if (!clientsData || clientsData.length === 0) return;
        const clientMap = new Map(clientsData.map(c => [c.id, c.name]));
        const activeClientIds = Array.from(clientMap.keys());

        // Determine which client IDs to scan:
        // If Domina: scan all active clients.
        // If Client: scan this client's tasks.
        let targetClientIds = activeClientIds;
        if (!isDomina && currentUserIdOrClientId && activeClientIds.includes(currentUserIdOrClientId)) {
            targetClientIds = [currentUserIdOrClientId];
        }

        // 2. Fetch tasks for target clients in the deadline inspection window:
        // - status in [PENDING, IN_PROGRESS]
        // - end_date_time between sevenDaysAgo and dueSoonWindow
        const { data: tasks = [] } = await supabase
            .from('task_instances')
            .select('id, client_id, title, end_date_time, status')
            .in('client_id', targetClientIds)
            .in('status', [TaskStatus.PENDING, TaskStatus.IN_PROGRESS])
            .gte('end_date_time', sevenDaysAgo)
            .lte('end_date_time', dueSoonWindow.toISOString());

        if (!tasks || tasks.length === 0) return;

        // 3. Fetch recent notifications (last 24 hours) for de-duplication to prevent spamming
        const recipientUserIds = new Set<string>();
        if (dominaId) recipientUserIds.add(dominaId);
        targetClientIds.forEach(id => recipientUserIds.add(id));

        const { data: recentNotifs = [] } = await supabase
            .from('app_notifications')
            .select('user_id, content')
            .in('user_id', Array.from(recipientUserIds))
            .gte('created_at', oneDayAgo);

        // Map recipient -> list of notification content strings
        const notifMap = new Map<string, string[]>();
        for (const n of recentNotifs || []) {
            if (!notifMap.has(n.user_id)) notifMap.set(n.user_id, []);
            notifMap.get(n.user_id)!.push(n.content);
        }

        for (const task of tasks) {
            const clientName = clientMap.get(task.client_id) || 'Ein Klient';
            const taskEndDate = new Date(task.end_date_time);
            const isOverdue = taskEndDate.getTime() < now.getTime();
            const isDueSoon = !isOverdue && taskEndDate.getTime() <= dueSoonWindow.getTime();

            if (!isOverdue && !isDueSoon) continue;

            const formattedTime = taskEndDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
            const formattedDate = taskEndDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

            // A) NOTIFY CLIENT
            const clientNotifs = notifMap.get(task.client_id) || [];
            if (isOverdue) {
                const cacheKey = `sub:${task.client_id}:${task.id}:overdue`;
                const alreadyNotified = recentlyNotifiedDeadlines.has(cacheKey) || 
                    clientNotifs.some(c => c.includes(task.title) && (c.includes('überfällig') || c.includes('Überfällig')));

                if (!alreadyNotified) {
                    recentlyNotifiedDeadlines.add(cacheKey);
                    const msg = `⚠️ Deine Aufgabe "${task.title}" ist überfällig (fällig war: ${formattedDate}, ${formattedTime} Uhr)!`;
                    await createAppNotification(task.client_id, msg, '/tasks');
                    clientNotifs.push(msg);
                }
            } else if (isDueSoon) {
                const cacheKey = `sub:${task.client_id}:${task.id}:dueSoon`;
                const alreadyNotified = recentlyNotifiedDeadlines.has(cacheKey) || 
                    clientNotifs.some(c => c.includes(task.title) && (c.includes('bald fällig') || c.includes('in Kürze fällig')));

                if (!alreadyNotified) {
                    recentlyNotifiedDeadlines.add(cacheKey);
                    const diffMinutes = Math.max(1, Math.round((taskEndDate.getTime() - now.getTime()) / 60000));
                    const timeRemainingStr = diffMinutes < 60 ? `in ${diffMinutes} Min.` : `um ${formattedTime} Uhr`;
                    const msg = `⏳ Deine Aufgabe "${task.title}" ist bald fällig (${timeRemainingStr})!`;
                    await createAppNotification(task.client_id, msg, '/tasks');
                    clientNotifs.push(msg);
                }
            }

            // B) NOTIFY DOMINA
            if (dominaId) {
                const dominaNotifs = notifMap.get(dominaId) || [];
                const dominaLink = `/clients/${task.client_id}?tab=tasks`;

                if (isOverdue) {
                    const cacheKey = `domina:${dominaId}:${task.id}:overdue`;
                    const alreadyNotified = recentlyNotifiedDeadlines.has(cacheKey) || 
                        dominaNotifs.some(c => c.includes(task.title) && c.includes(clientName) && (c.includes('überfällig') || c.includes('Überfällig')));

                    if (!alreadyNotified) {
                        recentlyNotifiedDeadlines.add(cacheKey);
                        const msg = `⚠️ Überfällige Aufgabe: ${clientName} hat "${task.title}" (fällig war: ${formattedDate}, ${formattedTime} Uhr) noch nicht erledigt!`;
                        await createAppNotification(dominaId, msg, dominaLink);
                        dominaNotifs.push(msg);
                    }
                } else if (isDueSoon) {
                    const cacheKey = `domina:${dominaId}:${task.id}:dueSoon`;
                    const alreadyNotified = recentlyNotifiedDeadlines.has(cacheKey) || 
                        dominaNotifs.some(c => c.includes(task.title) && c.includes(clientName) && (c.includes('bald fällig') || c.includes('in Kürze fällig')));

                    if (!alreadyNotified) {
                        recentlyNotifiedDeadlines.add(cacheKey);
                        const diffMinutes = Math.max(1, Math.round((taskEndDate.getTime() - now.getTime()) / 60000));
                        const timeRemainingStr = diffMinutes < 60 ? `in ${diffMinutes} Min.` : `um ${formattedTime} Uhr`;
                        const msg = `⏳ Aufgabe bald fällig: ${clientName} muss "${task.title}" bis ${timeRemainingStr} erledigen.`;
                        await createAppNotification(dominaId, msg, dominaLink);
                        dominaNotifs.push(msg);
                    }
                }
            }
        }
    } catch (e) {
        console.warn("checkAndNotifyTaskDeadlines failed", e);
    }
};

export const checkAndNotifyOverdueTasks = async (clientId: string): Promise<void> => {
    return checkAndNotifyTaskDeadlines(UserRole.SUB, clientId);
};
const isNotificationUuid = (val: unknown): val is string => 
    typeof val === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim());

export const markNotificationAsRead = async (id: string): Promise<void> => { 
    if (!isNotificationUuid(id)) return;
    await retryOperation(async () => { 
        const { error = null } = await supabase.from('app_notifications').update({ is_read: true }).eq('id', id); 
        if (error) throw error; 
    }); 
};

export const markAllNotificationsAsRead = async (uid: string): Promise<void> => { 
    let targetUid = uid;
    if (targetUid === 'domina') {
        const dId = await getDominaId();
        if (dId) targetUid = dId;
        else return;
    }
    if (!isNotificationUuid(targetUid)) return;

    await retryOperation(async () => { 
        const { error = null } = await supabase.from('app_notifications').update({ is_read: true }).eq('user_id', targetUid).eq('is_read', false); 
        if (error) throw error; 
    }); 
};

export const deleteAppNotification = async (id: string): Promise<void> => {
    if (!isNotificationUuid(id)) return;
    let directSuccess = false;
    try {
        const { error } = await supabase.from('app_notifications').delete().eq('id', id);
        if (!error) directSuccess = true;
    } catch (e) {
        console.warn("Direct Supabase delete failed, attempting server proxy", e);
    }

    if (!directSuccess) {
        try {
            await fetch(`/api/notifications/${id}`, { method: 'DELETE' });
        } catch (e) {
            console.warn("Server proxy delete notification also failed", e);
        }
    }
};

export const deleteAllAppNotifications = async (uids: string | string[], onlyRead = false): Promise<void> => {
    const rawList = Array.isArray(uids) ? uids : [uids];
    const resolvedList: string[] = [];

    for (const item of rawList) {
        if (item === 'domina') {
            const dId = await getDominaId();
            if (dId && isNotificationUuid(dId)) resolvedList.push(dId);
        } else if (isNotificationUuid(item)) {
            resolvedList.push(item);
        }
    }

    if (resolvedList.length === 0) return;

    for (const uid of resolvedList) {
        try {
            let q = supabase.from('app_notifications').delete().eq('user_id', uid);
            if (onlyRead) q = q.eq('is_read', true);
            await q;
        } catch (e) {
            console.warn("Direct deleteAll failed for", uid, e);
        }
    }

    try {
        await fetch('/api/notifications/delete-batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userIds: resolvedList, onlyRead })
        });
    } catch (e) {
        console.warn("Server proxy deleteAll failed for", resolvedList, e);
    }
};

// --- EMOTIONS ---

export const getEmotionDefinitions = async (): Promise<EmotionDefinition[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('emotion_definitions').select('*').order('sort_order');
        if (error) {
            console.warn("Could not fetch emotion definitions, using defaults", error);
            return [
                { id: '1', label: 'Suchtdruck', emoji: '💊', isDefault: true, sortOrder: 1 },
                { id: '2', label: 'Geilheit', emoji: '🔥', isDefault: true, sortOrder: 2 },
                { id: '3', label: 'Braucht Nähe', emoji: '🫂', isDefault: true, sortOrder: 3 },
                { id: '4', label: 'Ist verärgert', emoji: '😠', isDefault: true, sortOrder: 4 },
                { id: '5', label: 'Angst', emoji: '😨', isDefault: true, sortOrder: 5 },
                { id: '6', label: 'Traurig', emoji: '😢', isDefault: true, sortOrder: 6 },
                { id: '7', label: 'Hilflos', emoji: '🆘', isDefault: true, sortOrder: 7 },
                { id: '8', label: 'Braucht Strenge', emoji: '⛓️', isDefault: true, sortOrder: 8 },
            ];
        }
        return (data || []).map(d => ({
            id: d.id,
            label: d.label,
            emoji: d.emoji,
            isDefault: d.is_default,
            sortOrder: d.sort_order
        }));
    });
};

export const addEmotionDefinition = async (label: string, emoji: string): Promise<EmotionDefinition> => {
    return retryOperation(async () => {
        const { data: current } = await supabase.from('emotion_definitions').select('sort_order').order('sort_order', { ascending: false }).limit(1);
        const nextSort = (current?.[0]?.sort_order || 0) + 1;
        const { data, error } = await supabase.from('emotion_definitions').insert({
            label,
            emoji,
            is_default: false,
            sort_order: nextSort
        }).select().single();
        if (error) throw error;
        return {
            id: data.id,
            label: data.label,
            emoji: data.emoji,
            isDefault: data.is_default,
            sortOrder: data.sort_order
        };
    });
};

export const logEmotion = async (clientId: string, emotionId: string): Promise<void> => {
    return retryOperation(async () => {
        const { error } = await supabase.from('emotion_logs').insert({
            client_id: clientId,
            emotion_id: emotionId,
            timestamp: new Date().toISOString()
        });
        if (error) throw error;
        
        // Notify Domina
        try {
            const { data: client } = await supabase.from('clients').select('name').eq('id', clientId).single();
            const { data: emotion } = await supabase.from('emotion_definitions').select('label, emoji').eq('id', emotionId).maybeSingle();
            const label = emotion?.label || 'eine Emotion';
            const emoji = emotion?.emoji || '💭';
            
            const recipientId = await getDominaId();
            if (recipientId) {
                const msg = `${client?.name || 'Ein Klient'} meldet: ${label} ${emoji}`;
                await createAppNotification(recipientId, msg, `/clients/${clientId}`);
            }
        } catch (e) {
            console.warn("Failed to send emotion notification", e);
        }
    });
};

export const getTodayEmotionLogs = async (clientId: string): Promise<EmotionLog[]> => {
    return retryOperation(async () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const { data, error } = await supabase.from('emotion_logs')
            .select('*, emotion_definitions(label, emoji)')
            .eq('client_id', clientId)
            .gte('timestamp', today.toISOString())
            .order('timestamp', { ascending: false });
        if (error) throw error;
        return (data || []).map(d => ({
            id: d.id,
            clientId: d.client_id,
            emotionId: d.emotion_id,
            timestamp: d.timestamp,
            emotionLabel: d.emotion_definitions?.label,
            emotionEmoji: d.emotion_definitions?.emoji
        }));
    });
};

export const getAllTodayEmotionLogs = async (): Promise<EmotionLog[]> => {
    return retryOperation(async () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const { data, error } = await supabase.from('emotion_logs')
            .select('*, emotion_definitions(label, emoji)')
            .gte('timestamp', today.toISOString())
            .order('timestamp', { ascending: false });
        if (error) throw error;
        return (data || []).map(d => ({
            id: d.id,
            clientId: d.client_id,
            emotionId: d.emotion_id,
            timestamp: d.timestamp,
            emotionLabel: d.emotion_definitions?.label,
            emotionEmoji: d.emotion_definitions?.emoji
        }));
    });
};

export const getAllEmotionLogs = async (days: number = 365): Promise<EmotionLog[]> => {
    return retryOperation(async () => {
        const since = new Date();
        since.setDate(since.getDate() - days);
        const { data, error } = await supabase.from('emotion_logs')
            .select('*, emotion_definitions(label, emoji)')
            .gte('timestamp', since.toISOString())
            .order('timestamp', { ascending: false });
        if (error) throw error;
        return (data || []).map(d => ({
            id: d.id,
            clientId: d.client_id,
            emotionId: d.emotion_id,
            timestamp: d.timestamp,
            emotionLabel: d.emotion_definitions?.label,
            emotionEmoji: d.emotion_definitions?.emoji
        }));
    });
};

export const getEmotionStats = async (clientId: string, days: number = 7): Promise<EmotionStats[]> => {
    return retryOperation(async () => {
        const since = new Date();
        since.setDate(since.getDate() - days);
        
        const { data: logs, error } = await supabase.from('emotion_logs')
            .select('*, emotion_definitions(label, emoji)')
            .eq('client_id', clientId)
            .gte('timestamp', since.toISOString());
            
        if (error) throw error;
        
        const statsMap: Record<string, EmotionStats> = {};
        
        (logs || []).forEach(log => {
            const eid = log.emotion_id;
            if (!statsMap[eid]) {
                statsMap[eid] = {
                    emotionId: eid,
                    label: log.emotion_definitions?.label || 'Unbekannt',
                    emoji: log.emotion_definitions?.emoji || '❓',
                    count: 0,
                    lastLogged: log.timestamp
                };
            }
            statsMap[eid].count++;
            if (new Date(log.timestamp) > new Date(statsMap[eid].lastLogged || 0)) {
                statsMap[eid].lastLogged = log.timestamp;
            }
        });
        
        return Object.values(statsMap).sort((a, b) => b.count - a.count);
    });
};

// --- CONTROL MODULE ---

export const getControlConfigs = async (): Promise<ControlConfig[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_configs').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []).map(mapControlConfigFromDB);
    });
};

export const getControlConfigByClientId = async (clientId: string): Promise<ControlConfig | null> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_configs').select('*').eq('client_id', clientId).eq('is_active', true).maybeSingle();
        if (error) return null;
        return data ? mapControlConfigFromDB(data) : null;
    });
};

export const createControlConfig = async (config: Omit<ControlConfig, 'id' | 'createdAt' | 'updatedAt' | 'complianceScore' | 'status'>): Promise<ControlConfig> => {
    return retryOperation(async () => {
        const { data, error = null } = await supabase.from('control_configs').insert({
            client_id: config.clientId,
            start_date: config.startDate,
            duration_weeks: config.durationWeeks,
            active_areas: config.activeAreas,
            is_active: true,
            summary: config.summary || ''
        }).select().single();
        if (error) throw error;
        return mapControlConfigFromDB(data);
    });
};

export const updateControlConfig = async (id: string, updates: Partial<ControlConfig>): Promise<ControlConfig> => {
    return retryOperation(async () => {
        const dbUpdates: any = {};
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.complianceScore !== undefined) dbUpdates.compliance_score = updates.complianceScore;
        if (updates.activeAreas !== undefined) dbUpdates.active_areas = updates.activeAreas;
        if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
        if (updates.durationWeeks !== undefined) dbUpdates.duration_weeks = updates.durationWeeks;
        if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
        if (updates.summary !== undefined) dbUpdates.summary = updates.summary;
        
        dbUpdates.updated_at = new Date().toISOString();

        const { data, error } = await supabase.from('control_configs').update(dbUpdates).eq('id', id).select().single();
        if (error) throw error;
        return mapControlConfigFromDB(data);
    });
};

export const getControlGoals = async (configId: string): Promise<ControlGoal[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_goals').select('*').eq('config_id', configId);
        if (error) throw error;
        return (data || []).map(mapControlGoalFromDB);
    });
};

export const upsertControlGoal = async (goal: Omit<ControlGoal, 'id' | 'createdAt'> & { id?: string }): Promise<ControlGoal> => {
    return retryOperation(async () => {
        const dbGoal: any = {
            config_id: goal.configId,
            area: goal.area,
            title: goal.title,
            description: goal.description,
            is_required: goal.isRequired
        };
        if (goal.id) dbGoal.id = goal.id;

        const { data, error } = await supabase.from('control_goals').upsert(dbGoal).select().single();
        if (error) throw error;
        return mapControlGoalFromDB(data);
    });
};

export const deleteControlGoal = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error } = await supabase.from('control_goals').delete().eq('id', id);
        if (error) throw error;
    });
};

// --- FLASH ALERTS / BLITZ-KONTROLLEN ---

export const getFlashAlertConfigs = async (clientId?: string): Promise<FlashAlertConfig[]> => {
    return retryOperation(async () => {
        let q = supabase.from('control_alert_configs').select('*').order('created_at', { ascending: false });
        if (clientId) q = q.eq('client_id', clientId);
        
        const { data, error } = await q;
        if (error) throw error;
        return (data || []).map(mapFlashAlertConfigFromDB);
    });
};

export const upsertFlashAlertConfig = async (config: Partial<FlashAlertConfig>): Promise<FlashAlertConfig> => {
    return retryOperation(async () => {
        const dbData: any = {
            client_id: config.clientId,
            title: config.title,
            description: config.description,
            requires_photo: config.requiresPhoto,
            deadline_minutes: config.deadlineMinutes,
            trigger_type: config.triggerType,
            fixed_times: config.fixedTimes,
            random_frequency: config.randomFrequency,
            random_period: config.randomPeriod,
            random_start_time: config.randomStartTime,
            random_end_time: config.randomEndTime,
            start_date: config.startDate,
            end_date: config.endDate,
            is_active: config.isActive
        };

        if (config.id) {
            // Explicit UPDATE
            const { data, error } = await supabase.from('control_alert_configs')
                .update(dbData)
                .eq('id', config.id)
                .select()
                .single();
            if (error) {
                console.error("Update FlashAlertConfig Error:", error);
                throw error;
            }
            return mapFlashAlertConfigFromDB(data);
        } else {
            // Explicit INSERT
            const { data, error } = await supabase.from('control_alert_configs')
                .insert(dbData)
                .select()
                .single();
            if (error) {
                console.error("Insert FlashAlertConfig Error:", error);
                throw error;
            }
            return mapFlashAlertConfigFromDB(data);
        }
    });
};

export const getActiveFlashAlerts = async (clientId: string): Promise<FlashAlertInstance[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_alerts')
            .select('*')
            .eq('client_id', clientId)
            .eq('status', FlashAlertStatus.PENDING)
            .order('triggered_at', { ascending: true });
        
        if (error) throw error;
        return (data || []).map(mapFlashAlertInstanceFromDB);
    });
};

export const getFlashAlertHistory = async (clientId: string): Promise<FlashAlertInstance[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_alerts')
            .select('*')
            .eq('client_id', clientId)
            .order('triggered_at', { ascending: false })
            .limit(50);
        
        if (error) throw error;
        return (data || []).map(mapFlashAlertInstanceFromDB);
    });
};

export const deleteFlashAlertConfig = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        // Cascade delete handled by DB (ON DELETE CASCADE)
        const { error } = await supabase.from('control_alert_configs').delete().eq('id', id);
        if (error) throw error;
    });
};

export const deleteFlashAlertInstance = async (id: string): Promise<void> => {
    await retryOperation(async () => {
        const { error } = await supabase.from('control_alerts').delete().eq('id', id);
        if (error) throw error;
    });
};

export const updateFlashAlertInstance = async (id: string, updates: Partial<FlashAlertInstance>): Promise<void> => {
    await retryOperation(async () => {
        const dbData: any = {};
        if (updates.status) dbData.status = updates.status;
        if (updates.deadlineAt) dbData.deadline_at = updates.deadlineAt;
        if (updates.triggeredAt) dbData.triggered_at = updates.triggeredAt;
        
        const { error } = await supabase.from('control_alerts')
            .update(dbData)
            .eq('id', id);
        if (error) throw error;
    });
};

export const submitFlashAlert = async (alertId: string, photo?: File, comment?: string): Promise<void> => {
    return retryOperation(async () => {
        let photoUrl = undefined;
        if (photo) {
            const res = await uploadProofFile(photo, 'system-alerts');
            photoUrl = res.publicUrl;
        }

        const now = new Date();
        const { data: alertData, error: fetchError } = await supabase
            .from('control_alerts')
            .select('*')
            .eq('id', alertId)
            .single();
        if (fetchError) throw fetchError;

        let configData = null;
        if (alertData.config_id) {
            const { data: cData } = await supabase
                .from('control_alert_configs')
                .select('*')
                .eq('id', alertData.config_id)
                .maybeSingle();
            if (cData) configData = mapFlashAlertConfigFromDB(cData);
        }

        let clientName = 'Klient';
        if (alertData.client_id) {
            const { data: clData } = await supabase
                .from('clients')
                .select('name')
                .eq('id', alertData.client_id)
                .maybeSingle();
            if (clData?.name) clientName = clData.name;
        }

        const isLate = alertData.deadline_at && now > new Date(alertData.deadline_at);
        const status = isLate ? FlashAlertStatus.LATE : FlashAlertStatus.COMPLETED;
        
        const { error } = await supabase.from('control_alerts').update({
            status: status,
            completed_at: now.toISOString(),
            photo_url: photoUrl,
            client_comment: comment
        }).eq('id', alertId);

        if (error) throw error;

        const alertTitle = configData?.title || 'Blitz-Kontrolle';

        // 1. Post submission directly into the Messenger conversation
        try {
            const convo = await getOrCreateConversation(alertData.client_id, UserRole.SUB);
            if (convo) {
                const submissionText = `⚡ Blitz-Kontrolle eingereicht: ${alertTitle}${isLate ? ' (⚠️ Verspätet)' : ' (✅ Pünktlich)'}${comment ? `\n\nKommentar: "${comment}"` : ''}`;
                await sendMessage({
                    conversationId: convo.id,
                    sender: UserRole.SUB,
                    content: photoUrl ? JSON.stringify({ text: submissionText, file: { name: 'Beweisfoto.jpg', url: photoUrl } }) : submissionText,
                    type: photoUrl ? 'IMAGE' : 'TEXT'
                });
            }
        } catch (chatErr) {
            console.warn("Could not post flash alert submission to chat:", chatErr);
        }

        // 2. In-App Notification to Domina
        const notifText = isLate 
            ? `⚠️ ${clientName}: Beweis verspätet eingereicht - ${alertTitle}` 
            : `⚡ ${clientName}: Beweis pünktlich eingereicht - ${alertTitle}`;
        await createAppNotification('domina', notifText, '/');

        // 3. Push to Domina
        try {
            await triggerPushNotification('domina', notifText, '/');
        } catch { /* ignore */ }
    });
};

export const addFlashAlertDominaComment = async (alertId: string, comment: string): Promise<void> => {
    await retryOperation(async () => {
        const { error } = await supabase.from('control_alerts').update({ domina_comment: comment }).eq('id', alertId);
        if (error) throw error;

        // Post Domina feedback into Messenger chat and send in-app notification to client
        try {
            const { data: alertData } = await supabase.from('control_alerts').select('client_id, config_id').eq('id', alertId).single();
            if (alertData?.client_id) {
                let alertTitle = 'Blitz-Kontrolle';
                if (alertData.config_id) {
                    const { data: cData } = await supabase.from('control_alert_configs').select('title').eq('id', alertData.config_id).maybeSingle();
                    if (cData?.title) alertTitle = cData.title;
                }
                const convo = await getOrCreateConversation(alertData.client_id, UserRole.DOMINA);
                if (convo) {
                    await sendMessage({
                        conversationId: convo.id,
                        sender: UserRole.DOMINA,
                        content: `⚡ Feedback der Göttin zur Blitz-Kontrolle (${alertTitle}):\n\n"${comment}"`,
                        type: 'TEXT'
                    });
                }
                await createAppNotification(
                    alertData.client_id,
                    `⚡ Feedback der Göttin zur Blitz-Kontrolle: "${comment}"`,
                    '/'
                );
            }
        } catch (notifErr) {
            console.warn("Failed to notify client about Domina comment:", notifErr);
        }
    });
};

export const autoGenerateFlashAlerts = async (clientId: string, selectedDateStr?: string): Promise<void> => {
    return retryOperation(async () => {
        const now = new Date();
        const dateStr = selectedDateStr || toLocalDateStr(now);
        
        // 1. Get active configs
        const { data: configs = null } = await supabase.from('control_alert_configs')
            .select('*')
            .eq('client_id', clientId)
            .eq('is_active', true)
            .lte('start_date', dateStr)
            .or(`end_date.is.null,end_date.gte.${dateStr}`);
        
        if (!configs || configs.length === 0) return;

        for (const config of configs) {
            // Check if already generated for this date
            const startOfDay = `${dateStr}T00:00:00`;
            const endOfDay = `${dateStr}T23:59:59`;
            
            const { count } = await supabase.from('control_alerts')
                .select('*', { count: 'exact', head: true })
                .eq('config_id', config.id)
                .gte('triggered_at', startOfDay)
                .lte('triggered_at', endOfDay);
            
            if (count && count > 0) continue; // Already generated for today

            if (config.trigger_type === 'FIXED' && config.fixed_times) {
                for (const time of config.fixed_times) {
                    const triggeredAt = new Date(`${dateStr}T${time}:00`);
                    let deadlineAt = undefined;
                    if (config.deadline_minutes) {
                        deadlineAt = new Date(triggeredAt.getTime() + config.deadline_minutes * 60000);
                    }

                    await supabase.from('control_alerts').insert({
                        config_id: config.id,
                        client_id: clientId,
                        triggered_at: triggeredAt.toISOString(),
                        deadline_at: deadlineAt?.toISOString()
                    });
                }
            } else if (config.trigger_type === 'RANDOM') {
                let shotsToday = 0;
                if (config.random_period === 'DAY') {
                    shotsToday = config.random_frequency || 0;
                } else if (config.random_period === 'WEEK') {
                    const dailyAvg = (config.random_frequency || 0) / 7;
                    shotsToday = Math.floor(dailyAvg);
                    if (Math.random() < (dailyAvg % 1)) shotsToday += 1;
                } else if (config.random_period === 'MONTH') {
                    const dailyAvg = (config.random_frequency || 0) / 30;
                    shotsToday = Math.floor(dailyAvg);
                    if (Math.random() < (dailyAvg % 1)) shotsToday += 1;
                }

                const sHour = parseInt((config.random_start_time || '08:00').split(':')[0]);
                const eHour = parseInt((config.random_end_time || '22:00').split(':')[0]);

                for (let i = 0; i < shotsToday; i++) {
                    const hour = Math.floor(Math.random() * (eHour - sHour) + sHour);
                    const minute = Math.floor(Math.random() * 60);
                    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
                    
                    const triggeredAt = new Date(`${dateStr}T${timeStr}`);
                    let deadlineAt = undefined;
                    if (config.deadline_minutes) {
                        deadlineAt = new Date(triggeredAt.getTime() + config.deadline_minutes * 60000);
                    }

                    await supabase.from('control_alerts').insert({
                        config_id: config.id,
                        client_id: clientId,
                        triggered_at: triggeredAt.toISOString(),
                        deadline_at: deadlineAt?.toISOString()
                    });
                }
            }
        }
    });
};

export interface SendFlashAlertParams {
    clientId: string;
    title: string;
    description?: string;
    deadlineMinutes: number;
    requiresPhoto?: boolean;
}

export const sendFlashAlertDirect = async (params: SendFlashAlertParams): Promise<FlashAlertInstance> => {
    return retryOperation(async () => {
        const { clientId, title, description = '', deadlineMinutes = 15, requiresPhoto = true } = params;
        const now = new Date();
        const deadlineAt = new Date(now.getTime() + deadlineMinutes * 60000);

        // 1. Get or create a config so metadata is preserved
        let configId: string | null = null;
        try {
            const { data: existingConfigs } = await supabase
                .from('control_alert_configs')
                .select('id')
                .eq('client_id', clientId)
                .eq('title', title)
                .limit(1);

            if (existingConfigs && existingConfigs.length > 0) {
                configId = existingConfigs[0].id;
                await supabase.from('control_alert_configs').update({
                    description,
                    requires_photo: requiresPhoto,
                    deadline_minutes: deadlineMinutes,
                    is_active: true
                }).eq('id', configId);
            } else {
                const { data: newConfig } = await supabase
                    .from('control_alert_configs')
                    .insert({
                        client_id: clientId,
                        title,
                        description,
                        requires_photo: requiresPhoto,
                        deadline_minutes: deadlineMinutes,
                        trigger_type: 'RANDOM',
                        is_active: true
                    })
                    .select()
                    .single();
                if (newConfig) configId = newConfig.id;
            }
        } catch (confErr) {
            console.warn("Could not create/fetch config, proceeding with alert insert:", confErr);
        }

        // 2. Insert into control_alerts
        const { data: alertData, error: alertErr } = await supabase
            .from('control_alerts')
            .insert({
                config_id: configId,
                client_id: clientId,
                status: FlashAlertStatus.PENDING,
                triggered_at: now.toISOString(),
                deadline_at: deadlineAt.toISOString(),
                is_notified: false
            })
            .select()
            .single();

        if (alertErr) {
            console.error("Failed to insert control_alert:", alertErr);
            throw alertErr;
        }

        // 3. Client resolution
        let clientName = 'Klient';
        let clientUserId: string | null = null;
        try {
            const { data: cl } = await supabase.from('clients').select('name, user_id').eq('id', clientId).maybeSingle();
            if (cl?.name) clientName = cl.name;
            if (cl?.user_id) clientUserId = cl.user_id;
        } catch { /* ignore */ }

        // 4. Send chat message to Messenger
        try {
            const convo = await getOrCreateConversation(clientId, UserRole.DOMINA);
            if (convo) {
                const chatText = `🚨 BLITZ-KONTROLLE: ${title}\n\n📋 Anweisung: ${description || 'Sofort ausführen und Beweisfoto einreichen!'}\n⏱️ Frist: ${deadlineMinutes} Minuten ab jetzt!`;
                await sendMessage({
                    conversationId: convo.id,
                    sender: UserRole.DOMINA,
                    content: chatText,
                    type: 'TEXT'
                });
            }
        } catch (chatErr) {
            console.warn("Chat notification for flash alert failed:", chatErr);
        }

        // 5. In-App notification to Sub
        const subNotifMsg = `🚨 BLITZ-KONTROLLE: ${title} (Frist: ${deadlineMinutes} Min.)`;
        try {
            await createAppNotification(clientId, subNotifMsg, '/');
            if (clientUserId && clientUserId !== clientId) {
                await createAppNotification(clientUserId, subNotifMsg, '/');
            }
        } catch (notifErr) {
            console.warn("In-app notification to sub failed:", notifErr);
        }

        // 6. Push notification to Sub (Telegram)
        try {
            await triggerPushNotification(
                clientId,
                `🚨 BLITZ-KONTROLLE: ${title}\n\nFrist: ${deadlineMinutes} Minuten!\nSofort in der App Beweis einreichen!`,
                '/'
            );
        } catch (pushErr) {
            console.warn("Push to sub failed:", pushErr);
        }

        // 7. In-App notification to Domina
        const dominaNotifMsg = `⚡ Blitz-Kontrolle an ${clientName} gesendet: ${title} (Frist: ${deadlineMinutes} Min.)`;
        try {
            await createAppNotification('domina', dominaNotifMsg, '/');
            const { data: authData } = await supabase.auth.getUser();
            if (authData.user?.id) {
                await createAppNotification(authData.user.id, dominaNotifMsg, '/');
            }
        } catch (dominaNotifErr) {
            console.warn("In-app notification to Domina failed:", dominaNotifErr);
        }

        return mapFlashAlertInstanceFromDB(alertData);
    });
};

export const triggerFlashAlertNow = async (clientId: string, configId: string): Promise<void> => {
    return retryOperation(async () => {
        const { data: config } = await supabase.from('control_alert_configs').select('*').eq('id', configId).single();
        if (!config) throw new Error("Config not found");

        await sendFlashAlertDirect({
            clientId,
            title: config.title,
            description: config.description || undefined,
            deadlineMinutes: config.deadline_minutes || 15,
            requiresPhoto: config.requires_photo ?? true
        });
    });
};

export const getWeeklyChecks = async (configId: string): Promise<WeeklyControlCheck[]> => {
    return retryOperation(async () => {
        const { data, error } = await supabase.from('control_weekly_checks').select('*').eq('config_id', configId).order('week_number');
        if (error) throw error;
        return (data || []).map(mapWeeklyControlCheckFromDB);
    });
};

export const upsertWeeklyCheck = async (check: Omit<WeeklyControlCheck, 'id' | 'submittedAt'> & { id?: string }): Promise<WeeklyControlCheck> => {
    return retryOperation(async () => {
        const dbCheck: any = {
            config_id: check.configId,
            week_number: check.weekNumber,
            area_checks: check.areaChecks,
            revenue: check.revenue,
            energy_level: check.energyLevel,
            evening_energy_level: check.eveningEnergyLevel,
            is_completed: check.isCompleted,
            notes: check.notes,
            domina_notes: check.dominaNotes,
            submitted_at: new Date().toISOString()
        };
        if (check.id) dbCheck.id = check.id;

        const { data, error } = await supabase.from('control_weekly_checks').upsert(dbCheck, { onConflict: 'config_id,week_number' }).select().single();
        if (error) throw error;
        return mapWeeklyControlCheckFromDB(data);
    });
};
